import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import {
  Activity,
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  Clock3,
  Database,
  History,
  KeyRound,
  Lock,
  LogOut,
  Play,
  RefreshCw,
  Search,
  Server,
  ShieldCheck,
  StopCircle,
  Tags,
  TerminalSquare,
  Moon,
  Sun,
} from "lucide-react";
import "./App.css";

// Absolute so the dashboard works both at claude.revgenlabs.com AND when served
// under another domain (e.g. dashboard.revenue.inc/dashboard/infra via reverse proxy).
const API = "https://claude.revgenlabs.com/api";

const statusLabels = {
  success: "Success",
  partial: "Partial",
  failed: "Failed",
  running: "Running",
  cancelling: "Cancelling",
  cancelled: "Cancelled",
  idle: "Idle",
};

const periodOptions = [
  ["1d", "1D"],
  ["7d", "7D"],
  ["14d", "14D"],
  ["30d", "30D"],
];
// 6M comes from rollup_daily_infra + HLL sketches rather than raw events, which are
// pruned at 35 days. Only Infra Health knows how to serve it.
const periodOptionsWithLong = [...periodOptions, ["6m", "6M"]];

function periodLabel(period) {
  if (period === "custom") return "Custom";
  return periodOptionsWithLong.find(([key]) => key === period)?.[1] || "7D";
}

// Endpoints that still read raw events cannot answer 6m and now reject it with a 400.
// The period pills hide 6M on those sections, but fetchAll fans out to every dataset
// regardless of which section is open, so clamp it here too.
const rawPeriod = (period) => (period === "6m" ? "30d" : period);

const automationIcons = {
  "l10-scorecard-monitor": BarChart3,
  "eb-capacity-tracker": Activity,
  "campaign-progress-snapshot": BarChart3,
  "mailbox-limit-rotation": Tags,
  "find-domain": Search,
  "move-mailboxes": Database,
  "move-mailboxes-smtp": Server,
  "tag-mailboxes": Tags,
  "create-api-token": KeyRound,
  "reconnect-microsoft-mailboxes": RefreshCw,
  "reconnect-microsoft-workspace": RefreshCw,
};

function authHeader(token) {
  return { headers: { Authorization: `Bearer ${token}` } };
}

function fmtDate(value) {
  if (!value) return "Never";
  return `${format(new Date(value), "MMM d, HH:mm:ss")} PKT`;
}

function StatusPill({ status }) {
  return <span className={`status status-${status || "idle"}`}>{statusLabels[status] || status || "Idle"}</span>;
}

function LoginPage({ onLogin, onBack }) {
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    setError("");
    try {
      const r = await axios.post(`${API}/auth/login`, { username, password });
      localStorage.setItem("rgl_token", r.data.access_token);
      localStorage.setItem("rgl_user", r.data.username);
      localStorage.setItem("rgl_role", r.data.role || "admin");
      onLogin(r.data.access_token, r.data.username, r.data.role || "admin");
    } catch (e) {
      setError(e.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="login-screen">
      <section className="login-panel">
        <div className="login-mark"><ShieldCheck size={28} /></div>
        <h1>Admin sign in</h1>
        <p>Sign in with the admin account to run and manage VPS automations.</p>
        {error && <div className="form-error">{error}</div>}
        <label>
          Username
          <input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" />
        </label>
        <label>
          Password
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            autoComplete="current-password"
          />
        </label>
        <button className="primary-btn full" onClick={submit} disabled={loading}>
          <Lock size={16} />
          {loading ? "Signing in..." : "Sign in"}
        </button>
        {onBack && (
          <button className="ghost-btn" style={{ marginTop: 14 }} onClick={onBack}>
            ← Back to dashboard
          </button>
        )}
      </section>
    </main>
  );
}

function Metric({ icon: Icon, label, value, sub, tone }) {
  return (
    <div className={tone ? `metric metric-${tone}` : "metric"}>
      <Icon size={18} />
      <div>
        <strong>{value}</strong>
        <span>{label}</span>
        {sub && <small className="metric-sub">{sub}</small>}
      </div>
    </div>
  );
}

function summarizePath(path = "") {
  const parts = path.split("/").filter(Boolean);
  return parts.slice(-2).join("/");
}

function RunForm({ automation, onRun, disabled }) {
  const [dryRun, setDryRun] = useState(true);
  const [confirm, setConfirm] = useState(false);
  const [loop, setLoop] = useState(false);
  const [domain, setDomain] = useState("");
  const [tag, setTag] = useState("Outlook");
  const [targetWorkspace, setTargetWorkspace] = useState("");
  const [sourceApiKey, setSourceApiKey] = useState("");
  const [targetCount, setTargetCount] = useState("25");
  const [minAgeDays, setMinAgeDays] = useState("30");
  const [workspaceName, setWorkspaceName] = useState("");
  const [workspaceId, setWorkspaceId] = useState("");
  const [sourceWorkspace, setSourceWorkspace] = useState("");
  const [tokenName, setTokenName] = useState("Revenue.inc API");
  const [rotationExclude, setRotationExclude] = useState("Baton,Ocean,Raynmaker");

  const mode = automation.mode;
  const reconnectScope = automation.scope || (automation.key === "reconnect-microsoft-workspace" ? "workspace" : "all");
  const reconnectWorkspaceOnly = mode === "microsoft_reconnect" && reconnectScope === "workspace";
  const liveBlocked = (mode === "mailbox_move" || mode === "pv_mailbox_move" || mode === "microsoft_reconnect" || mode === "mailbox_rotation") && !dryRun && !confirm;

  const submit = () => {
    const inputs = {};
    if (mode === "domain_lookup") inputs.domain = domain;
    if (mode === "mailbox_move") {
      inputs.tag = tag;
      inputs.target_workspace = targetWorkspace;
      inputs.source_api_key = sourceApiKey;
      inputs.target_count = targetCount;
      inputs.min_age_days = minAgeDays;
    }
    if (mode === "pv_mailbox_move") {
      inputs.source_workspace = sourceWorkspace;
      inputs.target_workspace = targetWorkspace;
      inputs.tag = tag;
      inputs.target_count = targetCount;
      inputs.min_age_days = minAgeDays;
    }
    if (mode === "api_token") {
      inputs.workspace_name = workspaceName;
      inputs.workspace_id = workspaceId;
      inputs.token_name = tokenName;
    }
    if (mode === "microsoft_reconnect") {
      inputs.all_workspaces = !reconnectWorkspaceOnly;
      if (reconnectWorkspaceOnly) {
        inputs.workspace_name = workspaceName;
        inputs.workspace_id = workspaceId;
      }
    }
    if (mode === "mailbox_rotation") {
      inputs.exclude = rotationExclude;
    }
    onRun(automation.key, { inputs, dry_run: dryRun, loop, confirm });
  };

  if (mode === "no_input") {
    return (
      <div className="run-form compact">
        <button className="primary-btn" onClick={submit} disabled={disabled}>
          <Play size={16} />
          {automation.key === "eb-capacity-tracker" ? "Run capacity check" : "Run now"}
        </button>
      </div>
    );
  }

  return (
    <div className="run-form">
      {mode === "domain_lookup" && (
        <label>
          Domain
          <input placeholder="example.com" value={domain} onChange={(e) => setDomain(e.target.value)} />
        </label>
      )}

      {mode === "mailbox_move" && (
        <>
          <div className="field-grid">
            <label>
              Source tag
              <input value={tag} onChange={(e) => setTag(e.target.value)} />
            </label>
            <label>
              Target workspace
              <input placeholder="RGL - Saransh New" value={targetWorkspace} onChange={(e) => setTargetWorkspace(e.target.value)} />
            </label>
            <label>
              Count
              <input value={targetCount} onChange={(e) => setTargetCount(e.target.value)} />
            </label>
            <label>
              Min domain age
              <input value={minAgeDays} onChange={(e) => setMinAgeDays(e.target.value)} />
            </label>
          </div>
          <label>
            Source API key override
            <input placeholder="Optional; blank uses .env key" value={sourceApiKey} onChange={(e) => setSourceApiKey(e.target.value)} />
          </label>
          <div className="toggles">
            <button className={dryRun ? "toggle active" : "toggle"} onClick={() => setDryRun(true)} type="button">Dry run</button>
            <button className={!dryRun ? "toggle danger active" : "toggle danger"} onClick={() => setDryRun(false)} type="button">Live move</button>
            <label className="checkline"><input type="checkbox" checked={loop} onChange={(e) => setLoop(e.target.checked)} /> Loop batches</label>
          </div>
          {!dryRun && (
            <label className="checkline confirm">
              <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} />
              I understand this will move mailboxes on EmailBison.
            </label>
          )}
        </>
      )}

      {mode === "pv_mailbox_move" && (
        <>
          <div className="field-grid">
            <label>
              Source workspace
              <input placeholder="Baton Market" value={sourceWorkspace} onChange={(e) => setSourceWorkspace(e.target.value)} />
            </label>
            <label>
              Target workspace
              <input placeholder="MS warmed mailboxes" value={targetWorkspace} onChange={(e) => setTargetWorkspace(e.target.value)} />
            </label>
            <label>
              Tag
              <input value={tag} onChange={(e) => setTag(e.target.value)} />
            </label>
            <label>
              Count
              <input value={targetCount} onChange={(e) => setTargetCount(e.target.value)} />
            </label>
            <label>
              Min domain age
              <input value={minAgeDays} onChange={(e) => setMinAgeDays(e.target.value)} />
            </label>
          </div>
          <div className="toggles">
            <button className={dryRun ? "toggle active" : "toggle"} onClick={() => setDryRun(true)} type="button">Dry run</button>
            <button className={!dryRun ? "toggle danger active" : "toggle danger"} onClick={() => setDryRun(false)} type="button">Live move</button>
          </div>
          {!dryRun && (
            <label className="checkline confirm">
              <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} />
              I understand this will move mailboxes on PlusVibe.
            </label>
          )}
        </>
      )}

      {mode === "api_token" && (
        <>
          <label>
            Workspace name
            <input placeholder="Optima - Europe - 15K emails per month" value={workspaceName} onChange={(e) => setWorkspaceName(e.target.value)} />
          </label>
          <div className="field-grid">
            <label>
              Workspace ID
              <input placeholder="Optional, e.g. 166" value={workspaceId} onChange={(e) => setWorkspaceId(e.target.value)} />
            </label>
            <label>
              Token name
              <input value={tokenName} onChange={(e) => setTokenName(e.target.value)} />
            </label>
          </div>
          <p className="form-note">Use either workspace name or workspace ID. The created key is saved on the VPS with restricted permissions.</p>
        </>
      )}

      {mode === "microsoft_reconnect" && (
        <>
          {reconnectWorkspaceOnly ? (
            <>
              <label>
                Workspace name
                <input placeholder="Baton Market - 1MM emails per month" value={workspaceName} onChange={(e) => setWorkspaceName(e.target.value)} />
              </label>
              <label>
                Workspace ID
                <input placeholder="Optional, e.g. 14" value={workspaceId} onChange={(e) => setWorkspaceId(e.target.value)} />
              </label>
            </>
          ) : (
            <div className="form-banner">
              Scan all workspaces and process every disconnected Microsoft mailbox.
            </div>
          )}
          <div className="toggles">
            <button className={dryRun ? "toggle active" : "toggle"} onClick={() => setDryRun(true)} type="button">Dry run</button>
            <button className={!dryRun ? "toggle danger active" : "toggle danger"} onClick={() => setDryRun(false)} type="button">Live reconnect</button>
          </div>
          {!dryRun && (
            <label className="checkline confirm">
              <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} />
              I understand this will run Microsoft OAuth reconnect for matching mailboxes.
            </label>
          )}
          <p className="form-note">
            {reconnectWorkspaceOnly
              ? "Dry run lists disconnected Microsoft mailboxes in this workspace. Live reconnect uses the password sheet and processes all matches."
              : "Dry run lists disconnected Microsoft mailboxes across every workspace. Live reconnect uses the password sheet and processes all matches without page or mailbox limits."}
          </p>
        </>
      )}

      {mode === "mailbox_rotation" && (
        <>
          <div className="form-banner">
            Rotates all workspace mailboxes by sending domain, excluding selected clients.
          </div>
          <label>
            Exclude workspaces
            <input value={rotationExclude} onChange={(e) => setRotationExclude(e.target.value)} />
          </label>
          <div className="toggles">
            <button className={dryRun ? "toggle active" : "toggle"} onClick={() => setDryRun(true)} type="button">Dry run</button>
            <button className={!dryRun ? "toggle danger active" : "toggle danger"} onClick={() => setDryRun(false)} type="button">Live rotation</button>
          </div>
          {!dryRun && (
            <label className="checkline confirm">
              <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} />
              I understand this will update EmailBison sending and warmup limits.
            </label>
          )}
          <p className="form-note">Scheduled run: 1st and 16th monthly. Default excludes Baton, Ocean, and Raynmaker.</p>
        </>
      )}

      <button className={dryRun ? "primary-btn" : "danger-btn"} onClick={submit} disabled={disabled || liveBlocked}>
        <Play size={16} />
        {mode === "api_token" ? "Create API token" : mode === "microsoft_reconnect" && !dryRun ? "Run live reconnect" : mode === "mailbox_rotation" && !dryRun ? "Run live rotation" : dryRun ? "Run dry check" : "Run live automation"}
      </button>
    </div>
  );
}

function AutomationCard({ automation, onRun, onStop, running, stopping }) {
  const Icon = automationIcons[automation.key] || TerminalSquare;
  const canStop = automation.status === "running" && automation.active_run_id;
  return (
    <article className="automation-card">
      <div className="card-head">
        <div className="icon-box"><Icon size={20} /></div>
        <div>
          <h3>{automation.name}</h3>
          <p>{automation.description}</p>
        </div>
        <StatusPill status={automation.status} />
      </div>
      <div className="card-meta">
        <span>{summarizePath(automation.script_path)}</span>
        <span>{automation.python_exists ? "Runtime ready" : "Runtime missing"}</span>
      </div>
      {!automation.exists && <div className="warning"><AlertTriangle size={15} /> Script missing on VPS</div>}
      <RunForm automation={automation} onRun={onRun} disabled={running || !automation.exists || automation.status === "running"} />
      {canStop && (
        <button className="stop-btn full" onClick={() => onStop(automation.active_run_id)} disabled={stopping}>
          <StopCircle size={16} />
          {stopping ? "Stopping..." : "Stop running job"}
        </button>
      )}
      <div className="card-foot">
        <span>Last run: {fmtDate(automation.last_run)}</span>
        {automation.last_log?.duration_seconds && <span>{automation.last_log.duration_seconds}s</span>}
      </div>
      {automation.next_run ? (
        <div className="next-run">Next auto run: {fmtDate(automation.next_run)}</div>
      ) : (
        <div className="next-run muted">Manual — run on demand</div>
      )}
    </article>
  );
}

function LogsPanel({ logs, selectedLog, setSelectedLog, onStopLog, stoppingLogId }) {
  const active = selectedLog || logs[0];
  return (
    <section className="logs-panel">
      <div className="section-title">
        <History size={18} />
        <div>
          <h2>Automation Logs</h2>
          <p>Recent dashboard and scheduled automation runs</p>
        </div>
      </div>
      <div className="logs-layout">
        <div className="run-list">
          {logs.map((log) => (
            <button key={log.id} className={active?.id === log.id ? "run-item active" : "run-item"} onClick={() => setSelectedLog(log)}>
              <StatusPill status={log.status} />
              <strong>{log.job_name || "Automation"}</strong>
              <span>{fmtDate(log.started_at)}</span>
            </button>
          ))}
          {logs.length === 0 && <div className="empty-state">No automation runs yet.</div>}
        </div>
        <div className="log-output">
          {active ? (
            <>
              <div className="log-meta">
                <StatusPill status={active.status} />
                <span>Started {fmtDate(active.started_at)}</span>
                <span>{active.duration_seconds ? `${active.duration_seconds}s` : "In progress"}</span>
                {active.status === "running" && (
                  <button className="stop-btn compact" onClick={() => onStopLog(active)} disabled={stoppingLogId === active.id}>
                    <StopCircle size={14} />
                    {stoppingLogId === active.id ? "Stopping..." : "Stop run"}
                  </button>
                )}
              </div>
              <pre>{active.output || active.error || "No output captured yet."}</pre>
              {active.error && active.output && <pre className="error-output">{active.error}</pre>}
            </>
          ) : (
            <div className="empty-state">Select a run to inspect output.</div>
          )}
        </div>
      </div>
    </section>
  );
}

function Sparkline({ values, color }) {
  const nums = (values || []).map((v) => Number(v)).filter((v) => Number.isFinite(v));
  if (nums.length < 2) return null;
  const w = 96, h = 26, lo = Math.min(...nums), hi = Math.max(...nums), span = (hi - lo) || 1, step = w / (nums.length - 1);
  const pts = nums.map((v, i) => `${(i * step).toFixed(1)},${(h - 2 - ((v - lo) / span) * (h - 4)).toFixed(1)}`).join(" ");
  return (
    <svg className="sparkline" width={w} height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
      <polyline points={pts} fill="none" stroke={color || "#3564a4"} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function DeltaChip({ value, unit, goodIsUp }) {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  const flat = Math.abs(value) < 0.05;
  const up = value > 0;
  const good = flat ? null : (goodIsUp ? up : !up);
  const tone = good === null ? "flat" : good ? "up" : "down";
  const arrow = flat ? "\u2192" : up ? "\u2191" : "\u2193";
  const num = Math.abs(value).toFixed(unit === "pp" ? 2 : 1);
  return <span className={"delta-chip delta-" + tone} title="recent half vs earlier half of the period">{arrow} {num}{unit === "pp" ? "pp" : "%"}</span>;
}

function dailyTotals(buckets) {
  return (buckets || []).map((b) => {
    const t = Object.values(b.tags || {});
    const sum = (k) => t.reduce((acc, x) => acc + (Number(x[k]) || 0), 0);
    return { sent: sum("sent"), replies: sum("replies"), bounces: sum("bounces"), positives: sum("positives") };
  });
}

function halfRateDelta(days, numKey) {
  const rows = (days || []).filter((d) => d.sent > 0);
  if (rows.length < 4) return null;
  const mid = Math.floor(rows.length / 2);
  const rate = (arr) => { const s = arr.reduce((a, d) => a + d.sent, 0); const n = arr.reduce((a, d) => a + d[numKey], 0); return s ? (n / s) * 100 : 0; };
  return rate(rows.slice(mid)) - rate(rows.slice(0, mid));
}

function halfCountDelta(days, key) {
  const rows = days || [];
  if (rows.length < 4) return null;
  const mid = Math.floor(rows.length / 2);
  const avg = (arr) => arr.reduce((a, d) => a + (d[key] || 0), 0) / (arr.length || 1);
  const a = avg(rows.slice(0, mid)), b = avg(rows.slice(mid));
  if (!a) return null;
  return ((b - a) / a) * 100;
}

function ScoreBadge({ status }) {
  const label = status === "red" ? "Red" : status === "watch" ? "Watch" : status === "learning" ? "Learning" : "On track";
  return <span className={`score-badge score-${status || "ok"}`}>{label}</span>;
}

function numberFmt(value, digits = 0) {
  if (value === null || value === undefined || value === "") return "N/A";
  return Number(value).toLocaleString(undefined, { maximumFractionDigits: digits });
}

function InfraHealthPanel({ data, period }) {
  const totals = data?.totals || {};
  const peopleContacted = Number(totals.people_contacted || 0);
  const rows = data?.infra || [];
  const tags = data?.tags || [];
  const label = periodLabel(period);
  const risk = rows.find((row) => row.status === "red") || rows.find((row) => row.blocks || row.complaints);
  const tagsByGroup = tags.reduce((acc, row) => {
    const group = row.infra_group || "Unmapped";
    acc[group] = acc[group] || [];
    acc[group].push(row);
    return acc;
  }, {});
  const renderCpp = (row) => (
    <>
      {row.cpp ? `$${numberFmt(row.cpp, 2)}` : <span className="pending-pill">Waiting for positives</span>}
      {row.cost && <span>${numberFmt(row.cost, 2)} monthly cost</span>}
    </>
  );

  return (
    <section className="score-table-panel command-panel">
      <div className="section-title compact command-title">
        <Activity size={18} />
        <div>
          <h2>Infra Command Center</h2>
          <p>{label} unit economics from sends, webhook positives, and webhook bounces</p>
        </div>
      </div>
      <div className="command-grid">
        <div className="command-kpi">
          <span>Total sent</span>
          <strong>{numberFmt(totals.sent)}</strong>
          <small>{numberFmt(peopleContacted)} unique people contacted</small>
        </div>
        <div className="command-kpi">
          <span>Positive leads</span>
          <strong>{numberFmt(totals.positives)}</strong>
          <small>{totals.positive_source === "webhook" ? "Webhook source" : "Waiting for webhook positives"}</small>
        </div>
        <div className="command-kpi">
          <span>Unique repliers</span>
          <strong>{numberFmt(totals.unique_replies ?? totals.webhook_replies)}</strong>
          <small>{numberFmt(totals.webhook_replies)} reply events, {numberFmt(totals.unique_human_replies ?? totals.human_replies)} human</small>
        </div>
        <div className="command-kpi">
          <span>EPP</span>
          <strong>{numberFmt(totals.epp, 1)}</strong>
          <small>Emails per positive</small>
        </div>
        <div className="command-kpi muted">
          <span>CPP</span>
          <strong>{totals.cpp ? `$${numberFmt(totals.cpp, 2)}` : "Cost pending"}</strong>
          <small>{totals.cpp ? "Blended infra CPP" : "Waiting for cost inputs"}</small>
        </div>
      </div>
      <div className="insight-strip">
        <div>
          <span>Best tag EPP</span>
          <strong>Building baseline</strong>
        </div>
        <div>
          <span>Risk focus</span>
          <strong>{risk ? `${risk.infra_tag} needs review` : "No major infra risk"}</strong>
        </div>
        <div>
          <span>Cost model</span>
          <strong>{data?.cost_status === "partial" ? "Own + third-party configured" : "Not configured yet"}</strong>
        </div>
      </div>
      <div className="score-table-wrap">
        <table className="score-table infra-health-table">
          <thead>
            <tr>
              <th>Infra</th>
              <th>Group</th>
              <th>Sent</th>
              <th>Webhook positives</th>
              <th>EPP</th>
              <th>CPP</th>
              <th>Bounces</th>
              <th>Reply</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${row.infra_group}-${row.infra_tag}`}>
                <td>
                  <strong>{row.infra_tag}</strong>
                  <span>
                    {numberFmt(row.active_mailboxes ?? row.mailboxes)} active mailboxes
                    {row.warmup_mailboxes ? ` + ${numberFmt(row.warmup_mailboxes)} warmup` : ""}
                  </span>
                </td>
                <td>{row.infra_group}</td>
                <td>
                  {numberFmt(row.sent)}
                  <span>{row.sent_source === "webhook" ? "webhook" : "waiting"}</span>
                </td>
                <td>
                  {numberFmt(row.webhook_positives)}
                  <span>{row.positive_source}</span>
                </td>
                <td>{row.epp ? numberFmt(row.epp, 1) : <span className="pending-pill">Building</span>}</td>
                <td>
                  {renderCpp(row)}
                </td>
                <td>
                  {numberFmt(row.webhook_bounces)}
                  <span>{Number(row.bounce_rate || 0).toFixed(2)}% bounce rate</span>
                  <span>{numberFmt(row.hard_bounces)} hard, {numberFmt(row.blocks)} block</span>
                </td>
                <td>
                  {Number(row.reply_rate || 0).toFixed(2)}%
                  <span>{row.reply_source === "webhook" ? `${Number(row.human_reply_rate || 0).toFixed(2)}% human, ${Number(row.automated_reply_rate || 0).toFixed(2)}% OOO` : "EmailBison baseline"}</span>
                  {row.reply_source === "webhook" && <span>{numberFmt(row.unique_replies ?? row.webhook_replies)} unique, {numberFmt(row.webhook_replies)} events</span>}
                </td>
                <td>
                  <ScoreBadge status={row.status} />
                  {row.status_reason && <span>{row.status_reason}</span>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={9} className="table-empty">Infra health data will appear after scorecard and webhook data arrive.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="tag-drilldown">
        <div className="section-title compact">
          <Tags size={18} />
          <div>
            <h2>Infra Tag Drilldown</h2>
            <p>Same operating view by tag. CPP uses active mailbox cost; warmup pools are shown separately.</p>
          </div>
        </div>
        <div className="score-table-wrap">
          <table className="score-table infra-health-table infra-tag-table">
            <thead>
              <tr>
                <th>Infra</th>
                <th>Group</th>
                <th>Sent</th>
                <th>Webhook positives</th>
                <th>EPP</th>
                <th>CPP</th>
                <th>Bounces</th>
                <th>Reply</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(tagsByGroup).map(([group, groupRows]) => (
                <React.Fragment key={group}>
                  <tr className="group-divider">
                    <td colSpan={9}>{group}</td>
                  </tr>
                  {groupRows.map((row) => (
                    <tr key={`${row.infra_group}-${row.infra_tag}`}>
                      <td>
                        <strong>{row.infra_tag}</strong>
                        <span>
                          {numberFmt(row.active_mailboxes ?? row.mailboxes)} active mailboxes
                          {row.warmup_mailboxes ? ` + ${numberFmt(row.warmup_mailboxes)} warmup` : ""}
                        </span>
                      </td>
                      <td>{row.infra_group}</td>
                      <td>
                        {numberFmt(row.sent)}
                        <span>{row.sent_source === "webhook" ? "webhook" : "waiting"}</span>
                      </td>
                      <td>
                        {numberFmt(row.webhook_positives)}
                        <span>webhook</span>
                      </td>
                      <td>{row.epp ? numberFmt(row.epp, 1) : <span className="pending-pill">Building</span>}</td>
                      <td>
                        {renderCpp(row)}
                      </td>
                      <td>
                        {numberFmt(row.webhook_bounces)}
                        <span>{Number(row.bounce_rate || 0).toFixed(2)}% bounce rate</span>
                        <span>{numberFmt(row.hard_bounces)} hard, {numberFmt(row.blocks)} block</span>
                      </td>
                      <td>
                        {Number(row.reply_rate || 0).toFixed(2)}%
                        <span>{row.reply_source === "webhook" ? `${Number(row.human_reply_rate || 0).toFixed(2)}% human, ${Number(row.automated_reply_rate || 0).toFixed(2)}% OOO` : "EmailBison baseline"}</span>
                        {row.reply_source === "webhook" && <span>{numberFmt(row.unique_replies ?? row.webhook_replies)} unique, {numberFmt(row.webhook_replies)} events</span>}
                      </td>
                      <td>
                        <ScoreBadge status={row.status} />
                        {row.status_reason && <span>{row.status_reason}</span>}
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
              {tags.length === 0 && <tr><td colSpan={9} className="table-empty">Tag drilldown will appear as webhook events arrive.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function DomainIntelligencePanel({ data, onExecuteDomain, executingDomain }) {
  const summary = data?.summary || {};
  const senderDomains = data?.sender_domains || [];
  const recipientDomains = data?.recipient_domains || [];
  const urgentDomains = senderDomains.filter((row) => row.recommendation === "replace");
  const displayDomains = urgentDomains.length ? urgentDomains : senderDomains;
  const domainBadgeStatus = (recommendation) => {
    if (recommendation === "replace") return "red";
    if (recommendation === "watch") return "watch";
    if (recommendation === "learning") return "learning";
    return "ok";
  };
  return (
    <section className="score-table-panel domain-panel">
      <div className="section-title compact command-title">
        <ShieldCheck size={18} />
        <div>
          <h2>Domain Intelligence</h2>
          <p>Replacement and watchlist suggestions from bounce webhook patterns</p>
        </div>
      </div>
      <div className="domain-summary">
        <div>
          <span>Replace</span>
          <strong>{numberFmt(summary.replace)}</strong>
        </div>
        <div>
          <span>Watch</span>
          <strong>{numberFmt(summary.watch)}</strong>
        </div>
        <div>
          <span>Learning</span>
          <strong>{numberFmt(summary.learning)}</strong>
        </div>
        <div>
          <span>Healthy</span>
          <strong>{numberFmt(summary.ok)}</strong>
        </div>
      </div>
      <div className="score-table-wrap">
        <table className="score-table">
          <thead>
            <tr>
              <th>Sending domain</th>
              <th>Workspace</th>
              <th>Infra</th>
              <th>Sent</th>
              <th>RR</th>
              <th>Bounce</th>
              <th>Details</th>
              <th>Confidence</th>
              <th>Reason</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {displayDomains.map((row) => {
              const action = row.limit_action;
              const isExecuting = executingDomain === row.domain;
              return (
                <tr key={`${row.domain}-${row.infra_tag}`}>
                  <td>
                    <strong>{row.domain}</strong>
                    <span>{numberFmt(row.recipient_domain_count)} recipient domains</span>
                  </td>
                  <td className="workspace-cell">
                    {(row.workspaces || []).slice(0, 3).map((workspace) => (
                      <span key={`${row.domain}-${workspace.workspace_name}`}>
                        {workspace.workspace_name}
                        <em>{numberFmt(workspace.sent)} sent, {numberFmt(workspace.bounces)} bnc</em>
                      </span>
                    ))}
                    {Number(row.workspace_count || 0) > 3 && <span>+{numberFmt(Number(row.workspace_count) - 3)} more</span>}
                  </td>
                  <td>
                    {row.infra_tag}
                    <span>{row.infra_group}</span>
                  </td>
                  <td>{numberFmt(row.sent)}</td>
                  <td>
                    {Number(row.reply_rate || 0).toFixed(2)}%
                    <span>{numberFmt(row.replies)} replies, {numberFmt(row.automated_replies)} OOO</span>
                  </td>
                  <td>
                    {Number(row.bounce_rate || 0).toFixed(2)}%
                    <span>{numberFmt(row.bounces)} bounces</span>
                  </td>
                  <td>
                    <span>{numberFmt(row.complaints)} complaint/spam</span>
                    <span>{numberFmt(row.blocks)} blocks</span>
                    <span>{numberFmt(row.hard_bounces)} hard</span>
                    <span>{numberFmt(row.active_sending_days)} active days</span>
                    <span>peer bounce {Number(row.peer_bounce_rate || 0).toFixed(2)}%</span>
                  </td>
                  <td>
                    <span className={`confidence-pill confidence-${row.confidence || "low"}`}>{row.confidence || "low"}</span>
                    {row.sample_note && <span>{row.sample_note}</span>}
                  </td>
                  <td>
                    <ScoreBadge status={domainBadgeStatus(row.recommendation)} />
                    <span>{row.reason}</span>
                  </td>
                  <td className="domain-action-cell">
                    {onExecuteDomain && (
                      <button
                        className="domain-execute-btn"
                        onClick={() => onExecuteDomain(row.domain)}
                        disabled={isExecuting}
                      >
                        {isExecuting ? "Executing" : "Execute"}
                      </button>
                    )}
                    {action && (
                      <span className={`domain-action-note ${action.failed_count ? "has-failures" : ""}`}>
                        {action.failed_count ? `${numberFmt(action.updated_count)} updated, ${numberFmt(action.failed_count)} failed` : `${numberFmt(action.updated_count)} set for ${numberFmt(action.warmup_days)}d`}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
            {displayDomains.length === 0 && <tr><td colSpan={10} className="table-empty">No sender-domain bounce signals yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="mini-strip domain-strip">
        <strong>Recipient domains to suppress/review</strong>
        {recipientDomains.slice(0, 10).map((row) => (
          <span key={row.domain}>{row.domain}: {numberFmt(row.bounces)}</span>
        ))}
        {recipientDomains.length === 0 && <span>No recipient-domain signals yet</span>}
      </div>
    </section>
  );
}

function PositiveLeadsPanel({ data, period }) {
  const infra = data?.infra || [];
  const workspaces = data?.workspaces || [];
  const label = periodLabel(period);
  const sourceLabel = "Exact positive events received from EmailBison webhooks";
  return (
    <section className="score-table-panel positive-panel">
      <div className="section-title compact">
        <CheckCircle2 size={18} />
        <div>
          <h2>Positive Leads</h2>
          <p>{sourceLabel}</p>
        </div>
      </div>
      <div className="positive-grid">
        <div className="positive-total">
          <span>{label} positives</span>
          <strong>{Number(data?.total || 0).toLocaleString()}</strong>
        </div>
        <div className="positive-list">
          <h3>By infra</h3>
          {infra.map((row) => (
            <div className="positive-row" key={`${row.infra_group}-${row.infra_tag}`}>
              <span>{row.infra_tag} <small>{row.infra_group}</small></span>
              <strong>{Number(row.positives || 0).toLocaleString()}</strong>
            </div>
          ))}
          {infra.length === 0 && <div className="empty-state">No webhook positives stored yet.</div>}
        </div>
        <div className="positive-list">
          <h3>Workspaces with leads</h3>
          {workspaces.map((row) => (
            <div className="positive-row" key={row.workspace_name}>
              <span>{row.workspace_name}</span>
              <strong>{Number(row.positives || 0).toLocaleString()}</strong>
            </div>
          ))}
          {workspaces.length === 0 && <div className="empty-state">No workspace positives stored yet.</div>}
        </div>
      </div>
    </section>
  );
}

function BounceEventsPanel({ data, period }) {
  const categories = data?.categories || [];
  const infra = data?.infra || [];
  const domains = data?.recipient_domains || [];
  const label = periodLabel(period);
  return (
    <section className="score-table-panel positive-panel">
      <div className="section-title compact">
        <AlertTriangle size={18} />
        <div>
          <h2>Webhook Bounce Events</h2>
          <p>Exact bounce events received from EmailBison webhooks</p>
        </div>
      </div>
      <div className="positive-grid">
        <div className="positive-total">
          <span>{label} bounces</span>
          <strong>{Number(data?.total || 0).toLocaleString()}</strong>
        </div>
        <div className="positive-list">
          <h3>By type</h3>
          {categories.map((row) => (
            <div className="positive-row" key={row.bounce_category}>
              <span>{row.bounce_category.replace("_", " ")}</span>
              <strong>{Number(row.bounces || 0).toLocaleString()}</strong>
            </div>
          ))}
          {categories.length === 0 && <div className="empty-state">No webhook bounces stored yet.</div>}
        </div>
        <div className="positive-list">
          <h3>By infra</h3>
          {infra.slice(0, 6).map((row) => (
            <div className="positive-row" key={`${row.infra_group}-${row.infra_tag}`}>
              <span>{row.infra_tag} <small>{row.infra_group}</small></span>
              <strong>{Number(row.bounces || 0).toLocaleString()}</strong>
            </div>
          ))}
          {infra.length === 0 && <div className="empty-state">No infra bounces stored yet.</div>}
        </div>
      </div>
      {domains.length > 0 && (
        <div className="mini-strip">
          <strong>Top recipient domains</strong>
          {domains.slice(0, 6).map((row) => (
            <span key={row.recipient_domain}>{row.recipient_domain}: {Number(row.bounces || 0).toLocaleString()}</span>
          ))}
        </div>
      )}
    </section>
  );
}

const trendColors = ["#2563eb", "#10b981", "#8b5cf6", "#f59e0b", "#06b6d4", "#ef4444", "#ec4899"];
const trendColorMap = {
  AO: "#2563eb",
  "Outlook": "#10b981",
  GR: "#8b5cf6",
  "MD-SMTP": "#f59e0b",
  "RGL-SMTP": "#06b6d4",
  "G-NP": "#ef4444",
  "G-L": "#ec4899",
};

function trendColor(tag, index) {
  return trendColorMap[tag] || trendColors[index % trendColors.length];
}

function trendMetricValue(bucket, tag, metric) {
  const value = bucket?.tags?.[tag]?.[metric];
  return value === null || value === undefined ? null : Number(value);
}

function smoothPath(points) {
  if (!points.length) return "";
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
  const parts = [`M ${points[0].x} ${points[0].y}`];
  for (let index = 1; index < points.length; index += 1) {
    const previousPrevious = points[index - 2] || points[index - 1];
    const previous = points[index - 1];
    const current = points[index];
    const next = points[index + 1] || current;
    const smoothing = 0.18;
    const control1 = {
      x: previous.x + (current.x - previousPrevious.x) * smoothing,
      y: previous.y + (current.y - previousPrevious.y) * smoothing,
    };
    const control2 = {
      x: current.x - (next.x - previous.x) * smoothing,
      y: current.y - (next.y - previous.y) * smoothing,
    };
    parts.push(`C ${control1.x} ${control1.y}, ${control2.x} ${control2.y}, ${current.x} ${current.y}`);
  }
  return parts.join(" ");
}

function TrendLineChart({ title, subtitle, data, tags, metric, formatValue, target, emptyLabel, hiddenTags, onToggleTag }) {
  const buckets = data?.buckets || [];
  const [hoverIndex, setHoverIndex] = useState(null);
  const activeTags = tags.filter((tag) => buckets.some((bucket) => {
    const value = trendMetricValue(bucket, tag.infra_tag, metric);
    return value !== null && value > 0;
  }));
  const seriesTags = activeTags.length ? activeTags : tags.slice(0, 5);
  // Colour slot is keyed off the full `tags` list, not the filtered one, so hiding a
  // series never recolours the others and the filter chips match the chart lines.
  const colorSlot = new Map(tags.map((tag, index) => [tag.infra_tag, index]));
  const tagColor = (name) => trendColor(name, colorSlot.has(name) ? colorSlot.get(name) : 0);
  const isHidden = (name) => Boolean(hiddenTags && hiddenTags.has(name));
  const chartTags = seriesTags.filter((tag) => !isHidden(tag.infra_tag));
  const allHidden = seriesTags.length > 0 && chartTags.length === 0;
  const width = 820;
  const height = 280;
  const pad = { top: 24, right: 24, bottom: 42, left: 54 };
  const values = [];
  chartTags.forEach((tag) => {
    buckets.forEach((bucket) => {
      const value = trendMetricValue(bucket, tag.infra_tag, metric);
      if (value !== null) values.push(value);
    });
  });
  if (target) values.push(Number(target));
  const maxValue = Math.max(1, ...values) * 1.15;
  const xFor = (index) => pad.left + (buckets.length <= 1 ? 0 : (index / (buckets.length - 1)) * (width - pad.left - pad.right));
  const yFor = (value) => pad.top + (1 - (Number(value || 0) / maxValue)) * (height - pad.top - pad.bottom);
  const hoverBucket = hoverIndex !== null ? buckets[hoverIndex] : null;
  const hoverRows = hoverBucket ? chartTags
    .map((tag, index) => ({
      tag,
      index,
      value: trendMetricValue(hoverBucket, tag.infra_tag, metric),
    }))
    .filter((row) => row.value !== null)
    .sort((a, b) => Number(b.value || 0) - Number(a.value || 0)) : [];
  const grid = [0, 0.25, 0.5, 0.75, 1].map((ratio) => ({
    y: pad.top + ratio * (height - pad.top - pad.bottom),
    value: maxValue * (1 - ratio),
  }));

  return (
    <section className="trend-card">
      <div className="trend-card-head">
        <div>
          <h3>{title}</h3>
          <p>{subtitle}</p>
        </div>
      </div>
      <div className="trend-chart-wrap">
        {buckets.length === 0 || chartTags.length === 0 ? (
          <div className="trend-empty">{allHidden ? "All infra hidden for this chart. Use the Infra Filter above to bring some back." : (emptyLabel || "Trend data will appear as webhook events arrive.")}</div>
        ) : (
          <svg
            className="trend-chart"
            viewBox={`0 0 ${width} ${height}`}
            role="img"
            aria-label={title}
            onMouseLeave={() => setHoverIndex(null)}
          >
            <defs>
              <linearGradient id={`gridFade-${metric}`} x1="0" x2="1" y1="0" y2="0">
                <stop offset="0%" stopColor="#d9e5f7" />
                <stop offset="100%" stopColor="#eef4fb" />
              </linearGradient>
            </defs>
            {grid.map((line) => (
              <g key={line.y}>
                <line x1={pad.left} x2={width - pad.right} y1={line.y} y2={line.y} stroke={`url(#gridFade-${metric})`} strokeWidth="1" />
                <text x={pad.left - 12} y={line.y + 4} textAnchor="end" className="trend-axis-label">{formatValue(line.value)}</text>
              </g>
            ))}
            {target ? (
              <g>
                <line x1={pad.left} x2={width - pad.right} y1={yFor(target)} y2={yFor(target)} stroke="#e25555" strokeDasharray="5 5" strokeWidth="1.5" />
                <text x={width - pad.right} y={yFor(target) - 6} textAnchor="end" className="trend-target-label">target {formatValue(target)}</text>
              </g>
            ) : null}
            {chartTags.map((tag) => {
              const points = buckets
                .map((bucket, bucketIndex) => {
                  const value = trendMetricValue(bucket, tag.infra_tag, metric);
                  return value === null ? null : { x: xFor(bucketIndex), y: yFor(value) };
                })
                .filter(Boolean);
              const lastBucket = [...buckets].reverse().find((bucket) => trendMetricValue(bucket, tag.infra_tag, metric) !== null);
              const lastIndex = lastBucket ? buckets.indexOf(lastBucket) : -1;
              const lastValue = lastBucket ? trendMetricValue(lastBucket, tag.infra_tag, metric) : null;
              return (
                <g key={tag.infra_tag}>
                  <path d={smoothPath(points)} fill="none" stroke={tagColor(tag.infra_tag)} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  {lastValue !== null && lastIndex >= 0 && (
                    <circle cx={xFor(lastIndex)} cy={yFor(lastValue)} r="4" fill="#fff" stroke={tagColor(tag.infra_tag)} strokeWidth="2" />
                  )}
                </g>
              );
            })}
            {buckets.map((bucket, index) => {
              if (index !== 0 && index !== buckets.length - 1 && index % Math.ceil(buckets.length / 5) !== 0) return null;
              return <text key={bucket.bucket} x={xFor(index)} y={height - 14} textAnchor="middle" className="trend-axis-label">{bucket.label}</text>;
            })}
            {hoverBucket && (
              <line
                x1={xFor(hoverIndex)}
                x2={xFor(hoverIndex)}
                y1={pad.top}
                y2={height - pad.bottom}
                className="trend-hover-line"
              />
            )}
            {buckets.map((bucket, index) => (
              <rect
                key={`hit-${bucket.bucket}`}
                x={xFor(index) - ((width - pad.left - pad.right) / Math.max(1, buckets.length - 1)) / 2}
                y={pad.top}
                width={(width - pad.left - pad.right) / Math.max(1, buckets.length - 1)}
                height={height - pad.top - pad.bottom}
                fill="transparent"
                onMouseEnter={() => setHoverIndex(index)}
                onMouseMove={() => setHoverIndex(index)}
              >
                <title>
                  {[bucket.label, ...chartTags.map((tag) => `${tag.infra_tag}: ${formatValue(trendMetricValue(bucket, tag.infra_tag, metric) || 0)}`)].join("\n")}
                </title>
              </rect>
            ))}
          </svg>
        )}
        {hoverBucket && (
          <div
            className="trend-tooltip"
            style={{
              left: `${Math.min(84, Math.max(8, (xFor(hoverIndex) / width) * 100))}%`,
            }}
          >
            <strong>{hoverBucket.label}</strong>
            {hoverRows.map((row) => (
              <span key={row.tag.infra_tag}>
                <i style={{ background: tagColor(row.tag.infra_tag) }} />
                {row.tag.infra_tag}
                <b>{formatValue(row.value)}</b>
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="trend-legend">
        {seriesTags.map((tag) => {
          const latest = [...buckets].reverse().find((bucket) => Number(bucket?.tags?.[tag.infra_tag]?.sent || 0) > 0 && trendMetricValue(bucket, tag.infra_tag, metric) !== null);
          const value = latest ? trendMetricValue(latest, tag.infra_tag, metric) : null;
          const off = isHidden(tag.infra_tag);
          return (
            <span
              key={tag.infra_tag}
              className={`${off ? "is-off" : ""}${onToggleTag ? " is-clickable" : ""}`.trim() || undefined}
              onClick={onToggleTag ? (event) => onToggleTag(tag.infra_tag, event) : undefined}
              title={onToggleTag ? (off ? `Show ${tag.infra_tag}` : `Hide ${tag.infra_tag} (shift-click to show only this)`) : undefined}
            >
              <i style={{ background: tagColor(tag.infra_tag) }} />
              {tag.infra_tag}
              <b>{value === null ? "N/A" : formatValue(value)}</b>
            </span>
          );
        })}
      </div>
    </section>
  );
}

function InfraTrendsPanel({ data, period }) {
  const totals = data?.totals || {};
  const signals = data?.signals || [];
  const isUpdating = data?.loading || (data?.period && data.period !== period);
  const tags = (data?.tags || []).filter((tag) => Number(tag.sent || tag.replies || tag.bounces || tag.positives || 0) > 0);
  const label = periodLabel(period);
  const bucketLabel = data?.granularity === "hourly" || data?.bucket === "hour" ? "hourly" : "daily";
  const bucketSentence = bucketLabel === "hourly" ? "Hourly" : "Daywise";
  const sortedBySent = [...tags].sort((a, b) => Number(b.sent || 0) - Number(a.sent || 0)).slice(0, 10);
  const formatRate = (value) => `${Number(value || 0).toFixed(2)}%`;

  // Which infra series are hidden across every trend graph. Persisted so the choice
  // survives a reload and a period change.
  const [hiddenTags, setHiddenTags] = useState(() => {
    try {
      const saved = JSON.parse(window.localStorage.getItem("rgl.trends.hiddenInfra") || "[]");
      return new Set(Array.isArray(saved) ? saved : []);
    } catch (err) {
      return new Set();
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem("rgl.trends.hiddenInfra", JSON.stringify([...hiddenTags]));
    } catch (err) {
      /* storage blocked; the filter still works for this session */
    }
  }, [hiddenTags]);
  const allTagNames = sortedBySent.map((tag) => tag.infra_tag);
  const visibleCount = allTagNames.filter((name) => !hiddenTags.has(name)).length;
  const toggleTag = (name, event) => {
    const solo = Boolean(event && (event.shiftKey || event.altKey));
    setHiddenTags((prev) => {
      if (solo) {
        const others = allTagNames.filter((tag) => tag !== name);
        const alreadySolo = prev.size === others.length && others.every((tag) => prev.has(tag));
        return alreadySolo ? new Set() : new Set(others);   // shift-click again to restore
      }
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  return (
    <section className="trend-page">
      <div className="trend-overview">
        {isUpdating && <div className="trend-updating"><RefreshCw size={14} /> Updating trends for {periodLabel(period)}...</div>}
        <div>
          <span>{label} sent</span>
          <strong>{numberFmt(totals.sent)}</strong>
          <small>{bucketLabel} webhook rollup</small>
        </div>
        <div>
          <span>Human RR</span>
          <strong>{formatRate(totals.human_reply_rate)}</strong>
          <small>{numberFmt(totals.human_replies)} human replies</small>
        </div>
        <div>
          <span>OOO RR</span>
          <strong>{formatRate(totals.ooo_rate)}</strong>
          <small>{numberFmt(totals.ooo_replies)} automated replies</small>
        </div>
        <div>
          <span>Bounce / PLR</span>
          <strong>{formatRate(totals.bounce_rate)} / {formatRate(totals.positive_rate)}</strong>
          <small>{numberFmt(totals.bounces)} bounces, {numberFmt(totals.positives)} positives</small>
        </div>
      </div>

      <div className="trend-signals">
        <div>
          <h3>Health Signals</h3>
          <p>Only volume-backed alerts are shown here, so the chart stays useful instead of noisy.</p>
        </div>
        <div className="trend-signal-list">
          {signals.map((signal) => (
            <span key={`${signal.title}-${signal.detail}`} className={`trend-signal ${signal.severity}`}>
              <b>{signal.title}</b>
              <em>{signal.detail}</em>
            </span>
          ))}
          {signals.length === 0 && <span className="trend-signal ok"><b>No urgent infra signal</b><em>Current period is inside configured guardrails.</em></span>}
        </div>
      </div>

      {allTagNames.length > 0 && (
        <div className="trend-filter">
          <div className="trend-filter-head">
            <h3>Infra Filter</h3>
            <p>Click an infra to hide it. Shift-click to show only that one. Applies to every graph below.</p>
          </div>
          <div className="trend-filter-chips">
            {sortedBySent.map((tag, index) => {
              const off = hiddenTags.has(tag.infra_tag);
              return (
                <button
                  type="button"
                  key={tag.infra_tag}
                  className={`trend-chip${off ? " is-off" : ""}`}
                  onClick={(event) => toggleTag(tag.infra_tag, event)}
                  title={off ? `Show ${tag.infra_tag}` : `Hide ${tag.infra_tag} (shift-click to show only this)`}
                >
                  <i style={{ background: trendColor(tag.infra_tag, index) }} />
                  {tag.infra_tag}
                  <em>{numberFmt(tag.sent)}</em>
                </button>
              );
            })}
          </div>
          <div className="trend-filter-actions">
            <span>{visibleCount} of {allTagNames.length} shown</span>
            <button type="button" onClick={() => setHiddenTags(new Set())} disabled={hiddenTags.size === 0}>Show all</button>
            <button type="button" onClick={() => setHiddenTags(new Set(allTagNames))} disabled={visibleCount === 0}>Hide all</button>
          </div>
        </div>
      )}

      <div className="trend-grid">
        <TrendLineChart
          title="Human Reply Rate"
          subtitle={`${bucketSentence} human replies divided by webhook sends`}
          data={data}
          tags={sortedBySent}
          hiddenTags={hiddenTags}
          onToggleTag={toggleTag}
          metric="human_reply_rate"
          formatValue={formatRate}
          target={1}
        />
        <TrendLineChart
          title="OOO Reply Rate"
          subtitle={`${bucketSentence} automated replies, useful for inbox health and audience timing`}
          data={data}
          tags={sortedBySent}
          hiddenTags={hiddenTags}
          onToggleTag={toggleTag}
          metric="ooo_rate"
          formatValue={formatRate}
        />
        <TrendLineChart
          title="Bounce Pressure"
          subtitle={`${bucketSentence} webhook bounces divided by sends`}
          data={data}
          tags={sortedBySent}
          hiddenTags={hiddenTags}
          onToggleTag={toggleTag}
          metric="bounce_rate"
          formatValue={formatRate}
          target={2}
        />
        <TrendLineChart
          title="Overall Reply Rate"
          subtitle={`${bucketSentence} all replies (human + automated) divided by webhook sends`}
          data={data}
          tags={sortedBySent}
          hiddenTags={hiddenTags}
          onToggleTag={toggleTag}
          metric="reply_rate"
          formatValue={formatRate}
          target={3}
        />
      </div>
    </section>
  );
}

function CampaignBouncesTab({ data, period }) {
  const campaigns = data?.campaigns || [];
  const minSent = data?.min_sent || 100;
  const overall = data?.workspace_overall || {};
  const byWs = {};
  campaigns.forEach((c) => {
    (byWs[c.workspace_name] = byWs[c.workspace_name] || []).push(c);
  });
  const workspaces = Object.keys(byWs).map((name) => {
    const camps = byWs[name].slice().sort((a, b) => Number(b.bounce_rate || 0) - Number(a.bounce_rate || 0));
    const sent = camps.reduce((a, c) => a + Number(c.sent || 0), 0);
    const bounces = camps.reduce((a, c) => a + Number(c.bounces || 0), 0);
    return { name, camps, sent, bounces, rate: sent ? (bounces / sent) * 100 : 0 };
  }).sort((a, b) => ((overall[b.name]?.rate ?? b.rate) - (overall[a.name]?.rate ?? a.rate)));
  const [expanded, setExpanded] = useState({});
  return (
    <div className="details-clean">
      <section className="score-table-panel">
        <div className="section-title compact">
          <AlertTriangle size={18} />
          <div>
            <h2>Workspace Bounces</h2>
            <p>{periodLabel(period)} - campaigns above 2% bounce (min {numberFmt(minSent)} sent), grouped by workspace. Header totals cover the flagged campaigns only; "overall" is the workspace-wide rate.</p>
          </div>
        </div>
        <div className="score-table-wrap">
          <table className="score-table">
            <thead>
              <tr>
                <th>Workspace / Campaign</th>
                <th>Platform</th>
                <th>Sent</th>
                <th>Bounces</th>
                <th>Bounce %</th>
                <th>Top reason</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.length === 0 && <tr><td colSpan={6} className="table-empty">No workspaces above 2% bounce in this period.</td></tr>}
              {workspaces.map((ws) => (
                <React.Fragment key={`ws-${ws.name}`}>
                  <tr className="ws-bounce-head" style={{ cursor: "pointer" }} onClick={() => setExpanded((e) => ({ ...e, [ws.name]: !e[ws.name] }))}>
                    <td><span className="ws-namecell"><span className="ws-caret">{expanded[ws.name] ? "\u25be" : "\u25b8"}</span><strong>{ws.name}</strong></span></td>
                    <td className="cb-muted">workspace</td>
                    <td><strong>{numberFmt(ws.sent)}</strong></td>
                    <td><strong>{numberFmt(ws.bounces)}</strong></td>
                    <td>
                      <strong style={{ color: ws.rate >= 5 ? "#dc2626" : "#c2410c" }}>{ws.rate.toFixed(2)}%</strong>
                      {overall[ws.name] && (
                        <span className="cb-overall" title={`workspace-wide: ${numberFmt(overall[ws.name].bounces)} bounces / ${numberFmt(overall[ws.name].sent)} sent`} style={{ color: overall[ws.name].rate > 2 ? "#c2410c" : "#16a34a" }}>
                          overall {Number(overall[ws.name].rate).toFixed(2)}%
                        </span>
                      )}
                    </td>
                    <td className="cb-muted">{ws.camps.length} campaign{ws.camps.length > 1 ? "s" : ""}</td>
                  </tr>
                  {expanded[ws.name] && ws.camps.map((c) => (
                    <tr key={`cb-${c.campaign_id}`} className="ws-campaign-row">
                      <td className="cb-indent">{c.campaign_name}</td>
                      <td>{c.source === "plusvibe" ? "PV" : c.source === "emailbison" ? "EB" : (c.source || "-")}</td>
                      <td>{numberFmt(c.sent)}</td>
                      <td>{numberFmt(c.bounces)}</td>
                      <td><strong style={{ color: Number(c.bounce_rate) >= 5 ? "#dc2626" : "#c2410c" }}>{Number(c.bounce_rate).toFixed(2)}%</strong></td>
                      <td>{c.top_reason || "-"}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function DetailsTab({ workspaces, period, health }) {
  const tags = health?.tags || [];
  const inv = health?.pv_inventory || { tags: [], by_tag: [], by_client: [], by_provider: [] };
  const workspaceRows = [...workspaces].sort((a, b) => {
    const activeA = Number(a.sent_delta || 0) > 0;
    const activeB = Number(b.sent_delta || 0) > 0;
    const rank = (row) => {
      if (row.bounce_status === "red" || row.reply_status === "red") return 0;
      if (row.reply_status === "watch") return 1;
      return 2;
    };
    return Number(activeB) - Number(activeA)
      || rank(a) - rank(b)
      || Number(a.reply_rate || 0) - Number(b.reply_rate || 0)
      || Number(b.bounce_rate || 0) - Number(a.bounce_rate || 0)
      || Number(b.sent_delta || 0) - Number(a.sent_delta || 0)
      || String(a.workspace_name || "").localeCompare(String(b.workspace_name || ""));
  });
  const tagRows = [...tags].sort((a, b) => {
    const cppA = Number(a.cpp || 0);
    const cppB = Number(b.cpp || 0);
    const hasCppA = cppA > 0;
    const hasCppB = cppB > 0;
    return Number(hasCppB) - Number(hasCppA)
      || cppA - cppB
      || Number(a.epp || 999999) - Number(b.epp || 999999)
      || Number(b.webhook_positives || 0) - Number(a.webhook_positives || 0)
      || String(a.infra_tag || "").localeCompare(String(b.infra_tag || ""));
  });

  return (
    <div className="details-clean">
      <section className="score-table-panel">
        <div className="section-title compact">
          <Tags size={18} />
          <div>
            <h2>Infra Tag Performance</h2>
            <p>{periodLabel(period)} sending infra, sorted by lowest CPP first</p>
          </div>
        </div>
        <div className="score-table-wrap">
          <table className="score-table baseline-table">
            <thead>
              <tr>
                <th>Infra</th>
                <th>Sent</th>
                <th>Positives</th>
                <th>EPP</th>
                <th>CPP</th>
                <th>Bounce</th>
                <th>Reply</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {tagRows.map((row) => (
                <tr key={`${row.infra_group}-${row.infra_tag}`}>
                  <td>
                    <strong>{row.infra_tag}</strong>
                    <span>{numberFmt(row.active_mailboxes ?? row.mailboxes)} active mailboxes</span>
                  </td>
                  <td>{numberFmt(row.sent)}</td>
                  <td>{numberFmt(row.webhook_positives)}</td>
                  <td>{row.epp ? numberFmt(row.epp, 1) : "Building"}</td>
                  <td>
                    {row.cpp ? `$${numberFmt(row.cpp, 2)}` : "Waiting"}
                    {row.cost && <span>${numberFmt(row.cost, 2)} monthly</span>}
                  </td>
                  <td>{Number(row.bounce_rate || 0).toFixed(2)}%</td>
                  <td>
                    {Number(row.reply_rate || 0).toFixed(2)}%
                    <span>{numberFmt(row.unique_replies ?? row.webhook_replies)} unique, {numberFmt(row.webhook_replies)} events</span>
                  </td>
                  <td><ScoreBadge status={row.status} /></td>
                </tr>
              ))}
              {tagRows.length === 0 && <tr><td colSpan={8} className="table-empty">Infra tag data appears after webhook events arrive.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <section className="score-table-panel">
        <div className="section-title compact">
          <Tags size={18} />
          <div>
            <h2>Mailboxes by Provider</h2>
            <p>PlusVibe + retained EB mailboxes grouped by email provider (Microsoft / Google / SMTP)</p>
          </div>
        </div>
        {inv.surplus_summary && inv.surplus_summary.total > 0 && (
          <div className="surplus-strip">
            <div className="surplus-cell">
              <span>Deployed to clients</span>
              <strong>{numberFmt(inv.surplus_summary.deployed)}</strong>
              <small>{inv.surplus_summary.deployed_pct}% of fleet - {numberFmt(inv.surplus_summary.deployed_capacity)}/day</small>
            </div>
            <div className="surplus-cell warn">
              <span>Surplus (warm-up pool)</span>
              <strong>{numberFmt(inv.surplus_summary.surplus)}</strong>
              <small>{inv.surplus_summary.surplus_pct}% of fleet - {numberFmt(inv.surplus_summary.surplus_capacity)}/day idle</small>
            </div>
            <div className="surplus-cell muted">
              <span>Warm-up pools</span>
              <strong>{(inv.surplus_summary.pools || []).length}</strong>
              <small>{(inv.surplus_summary.pools || []).map((p) => `${p.workspace} ${numberFmt(p.mailboxes)}`).join(" | ") || "none"}</small>
            </div>
          </div>
        )}
        <div className="score-table-wrap">
          <table className="score-table">
            <thead>
              <tr><th>Provider</th><th>Active</th><th>Issues</th><th>Deployed</th><th>Surplus</th><th>Total</th><th>Share</th><th>Daily Capacity</th></tr>
            </thead>
            <tbody>
              {(inv.by_provider || []).map((row) => (
                <tr key={`mbxprov-${row.provider}`}>
                  <td><strong>{row.provider}</strong></td>
                  <td>{numberFmt(row.active)}</td>
                  <td>{row.issues ? numberFmt(row.issues) : "-"}</td>
                  <td>{numberFmt(row.deployed)}</td>
                  <td>
                    {row.surplus ? numberFmt(row.surplus) : "-"}
                    {row.surplus ? <span className="surplus-pct">{row.surplus_pct}%</span> : null}
                  </td>
                  <td>{numberFmt(row.total)}</td>
                  <td>{row.share != null ? `${row.share}%` : "-"}</td>
                  <td>{numberFmt(row.capacity)}</td>
                </tr>
              ))}
              {(inv.by_provider || []).length > 0 && (
                <tr style={{ borderTop: "2px solid #cbd5e1", fontWeight: 700 }}>
                  <td><strong>Total</strong></td>
                  <td><strong>{numberFmt(inv.by_provider.reduce((a, r) => a + Number(r.active || 0), 0))}</strong></td>
                  <td><strong>{numberFmt(inv.by_provider.reduce((a, r) => a + Number(r.issues || 0), 0))}</strong></td>
                  <td><strong>{numberFmt(inv.by_provider.reduce((a, r) => a + Number(r.deployed || 0), 0))}</strong></td>
                  <td><strong>{numberFmt(inv.by_provider.reduce((a, r) => a + Number(r.surplus || 0), 0))}</strong></td>
                  <td><strong>{numberFmt(inv.by_provider.reduce((a, r) => a + Number(r.total || 0), 0))}</strong></td>
                  <td><strong>100%</strong></td>
                  <td><strong>{numberFmt(inv.by_provider.reduce((a, r) => a + Number(r.capacity || 0), 0))}</strong></td>
                </tr>
              )}
              {(inv.by_provider || []).length === 0 && <tr><td colSpan={8} className="table-empty">No PlusVibe mailbox data.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <section className="score-table-panel">
        <div className="section-title compact">
          <Tags size={18} />
          <div>
            <h2>Mailboxes by Tag</h2>
            <p>Live PlusVibe mailbox inventory per infra tag, all workspaces</p>
          </div>
        </div>
        <div className="score-table-wrap">
          <table className="score-table">
            <thead>
              <tr><th>Infra Tag</th><th>Active</th><th>Issues</th><th>Total</th></tr>
            </thead>
            <tbody>
              {inv.by_tag.map((row) => (
                <tr key={`mbxtag-${row.infra_tag}`}>
                  <td><strong>{row.infra_tag}</strong></td>
                  <td>{numberFmt(row.active)}</td>
                  <td>{row.issues ? numberFmt(row.issues) : "-"}</td>
                  <td>{numberFmt(row.total)}</td>
                </tr>
              ))}
              {inv.by_tag.length > 0 && (
                <tr style={{ borderTop: "2px solid #cbd5e1", fontWeight: 700 }}>
                  <td><strong>Total</strong></td>
                  <td><strong>{numberFmt(inv.by_tag.reduce((a, r) => a + Number(r.active || 0), 0))}</strong></td>
                  <td><strong>{numberFmt(inv.by_tag.reduce((a, r) => a + Number(r.issues || 0), 0))}</strong></td>
                  <td><strong>{numberFmt(inv.by_tag.reduce((a, r) => a + Number(r.total || 0), 0))}</strong></td>
                </tr>
              )}
              {inv.by_tag.length === 0 && <tr><td colSpan={4} className="table-empty">No PlusVibe mailbox data.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <section className="score-table-panel">
        <div className="section-title compact">
          <Tags size={18} />
          <div>
            <h2>Mailboxes by Client</h2>
            <p>PlusVibe mailbox count per client, broken down by infra tag, with daily sending capacity</p>
          </div>
        </div>
        <div className="score-table-wrap">
          <table className="score-table">
            <thead>
              <tr>
                <th>Client</th>
                {inv.tags.map((t) => <th key={`mbxch-${t}`}>{t}</th>)}
                <th>Total</th>
                <th>Capacity /day</th>
              </tr>
            </thead>
            <tbody>
              {inv.by_client.map((row) => (
                <tr key={`mbxcli-${row.client}`}>
                  <td><strong>{row.client}</strong></td>
                  {inv.tags.map((t) => <td key={`mbxc-${row.client}-${t}`}>{row.counts[t] ? numberFmt(row.counts[t]) : "-"}</td>)}
                  <td><strong>{numberFmt(row.total)}</strong></td>
                  <td><strong>{row.capacity ? numberFmt(row.capacity) : "-"}</strong></td>
                </tr>
              ))}
              {inv.by_client.length > 0 && (
                <tr style={{ borderTop: "2px solid #cbd5e1", fontWeight: 700 }}>
                  <td><strong>Total</strong></td>
                  {inv.tags.map((t) => <td key={`mbxct-${t}`}><strong>{numberFmt(inv.by_client.reduce((a, r) => a + Number(r.counts[t] || 0), 0))}</strong></td>)}
                  <td><strong>{numberFmt(inv.by_client.reduce((a, r) => a + Number(r.total || 0), 0))}</strong></td>
                  <td><strong>{numberFmt(inv.by_client.reduce((a, r) => a + Number(r.capacity || 0), 0))}</strong></td>
                </tr>
              )}
              {inv.by_client.length === 0 && <tr><td colSpan={inv.tags.length + 3} className="table-empty">No PlusVibe mailbox data.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

/* ------------------------------------------------------------ CPP (cost per positive)
   Real CPP = infra cost + lead cost + overheads, per positive.
     bucket 1  infra    tenants/panels + domains + mailboxes, normalised to a month
     bucket 2  lead     data needed x (source + enrichment + verification)
     bucket 3  overhead per-email sequencer fee + a share of fixed monthly overheads
   Mailboxes, emails sent and positives are live per infra tag and can each be
   overridden. Everything recomputes in the browser as you type. Admin only. */
const cppFmt = (v, d = 2) =>
  v === null || v === undefined || Number.isNaN(Number(v))
    ? "—"
    : Number(v).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
const cppInt = (v) =>
  v === null || v === undefined || Number.isNaN(Number(v))
    ? "—"
    : Math.round(Number(v)).toLocaleString();
const cppMoney = (v, d = 2) => (v === null || v === undefined ? "—" : `$${cppFmt(v, d)}`);

function cppCalc(p, mbx, emails, positives, overheadAlloc, windowMonths) {
  const f = (k) => Number(p[k]) || 0;
  const m = Number(mbx) || 0;
  const e = Number(emails) || 0;
  const pos = Number(positives) || 0;
  const dpp = f("domains_per_panel");
  const mpd = f("mailboxes_per_domain");
  const panels = dpp && mpd ? m / (dpp * mpd) : 0;
  const domains = mpd ? m / mpd : 0;
  const wm = Number(windowMonths) || 1;
  // infra is a MONTHLY cost; emails and positives are whatever the window produced,
  // so pro-rate infra (and the fixed overheads below) onto the same time basis
  const panelTerm = (f("panel_period") ? panels * (f("panel_cost") / f("panel_period")) : 0) * wm;
  const domainTerm = (f("domain_period") ? domains * (f("domain_cost") / f("domain_period")) : 0) * wm;
  const mbxTerm = (f("mailbox_period") ? m * (f("mailbox_cost") / f("mailbox_period")) : 0) * wm;
  const infra = panelTerm + domainTerm + mbxTerm;
  const dataNeeded = f("avg_touchpoint") && f("avg_source_ratio") ? e / f("avg_touchpoint") / f("avg_source_ratio") : 0;
  const leadRate = f("source_cost") + f("enrichment_cost") + f("verification_cost");
  const sourceSpend = dataNeeded * f("source_cost");
  const enrichSpend = dataNeeded * f("enrichment_cost");
  const verifySpend = dataNeeded * f("verification_cost");
  const lead = sourceSpend + enrichSpend + verifySpend;
  const sequencer = f("sending_cost") * e;
  const alloc = (Number(overheadAlloc) || 0) * wm;
  const overhead = sequencer + alloc;
  const total = infra + lead + overhead;
  return {
    panels, domains, panelTerm, domainTerm, mbxTerm, infra,
    dataNeeded, leadRate, sourceSpend, enrichSpend, verifySpend, lead,
    sequencer, alloc, overhead, other: lead + overhead, total,
    cpp: pos ? total / pos : null,
    cppInfra: pos ? infra / pos : null,
    cppLead: pos ? lead / pos : null,
    cppOverhead: pos ? overhead / pos : null,
    perMailbox: m ? infra / m : null,
    perLead: dataNeeded ? lead / dataNeeded : null,
    epp: pos ? e / pos : null,
  };
}

/* split each fixed overhead across rows on its own basis; equal split if the basis is empty */
function cppAllocate(overheads, used) {
  const keys = Object.keys(used);
  const alloc = {};
  keys.forEach((k) => { alloc[k] = 0; });
  (overheads || []).forEach((oh) => {
    const amt = Number(oh.amount) || 0;
    let w = {};
    if (oh.basis === "equal") keys.forEach((k) => { w[k] = 1; });
    else keys.forEach((k) => { w[k] = Number(used[k][oh.basis]) || 0; });
    let tot = keys.reduce((s, k) => s + w[k], 0);
    if (tot <= 0) { keys.forEach((k) => { w[k] = 1; }); tot = keys.length || 1; }
    keys.forEach((k) => { alloc[k] += (amt * w[k]) / tot; });
  });
  return alloc;
}

const CPP_INFRA_COLS = [
  { k: "panel_cost", h: "Panel / tenant", sub: "$ per panel", step: "0.01" },
  { k: "panel_period", h: "Period", sub: "months", step: "1" },
  { k: "domain_cost", h: "Domain", sub: "$ per domain", step: "0.01" },
  { k: "domain_period", h: "Period", sub: "months", step: "1" },
  { k: "mailbox_cost", h: "Mailbox", sub: "$ per mailbox", step: "0.0001" },
  { k: "mailbox_period", h: "Period", sub: "months", step: "1" },
  { k: "domains_per_panel", h: "Domains / panel", sub: "count", step: "1" },
  { k: "mailboxes_per_domain", h: "Mailboxes / domain", sub: "count", step: "1" },
];
const CPP_LEAD_COLS = [
  { k: "source_cost", h: "Source", sub: "$ per record", step: "0.0001" },
  { k: "enrichment_cost", h: "Enrichment", sub: "$ per record", step: "0.0001" },
  { k: "verification_cost", h: "Verification", sub: "$ per record", step: "0.0001" },
];
const CPP_BASIS_LABEL = { emails: "Emails sent", mailboxes: "Mailboxes", positives: "Positives", equal: "Equal split" };

function CppTab({ token, isAdmin }) {
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [ohDraft, setOhDraft] = useState(null);
  const [days, setDays] = useState(30);
  const [showSub, setShowSub] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    const cppUrl = isAdmin ? `${API}/cpp?days=${days}` : `${API}/cpp/summary?days=${days}`;
    axios.get(cppUrl, authHeader(token))
      .then((r) => {
        setData(r.data);
        setEdits({});
        setOhDraft((r.data.overheads || []).map((o) => ({ id: o.id, label: o.label, amount: o.amount, basis: o.basis })));
        setMsg("");
      })
      .catch((e) => setMsg(e?.response?.status === 403 ? "Admin access required." : "Could not load CPP data."))
      .finally(() => setLoading(false));
  }, [token, days, isAdmin]);
  useEffect(() => { load(); }, [load]);

  const setField = (rowKey, field, value) =>
    setEdits((prev) => ({ ...prev, [rowKey]: { ...(prev[rowKey] || {}), [field]: value } }));

  const view = useMemo(() => {
    // data is the SUMMARY payload for non-admins (no rows/overheads), so bail out before
    // touching the full-model fields. This memo runs before the viewer early-return because
    // hooks cannot be conditional.
    if (!data || !Array.isArray(data.rows)) return null;
    const resolved = data.rows.map((r) => {
      const e = edits[r.key] || {};
      const params = { ...r.params };
      Object.keys(e).forEach((k) => {
        if (!k.startsWith("ov_") && e[k] !== "" && e[k] !== null && e[k] !== undefined) params[k] = Number(e[k]);
      });
      const ovVal = (k) => (Object.prototype.hasOwnProperty.call(e, k) ? (e[k] === "" ? null : Number(e[k])) : r.override[k]);
      const used = {
        mailboxes: ovVal("ov_mailboxes") ?? r.live.mailboxes,
        emails: ovVal("ov_emails") ?? r.live.emails,
        positives: ovVal("ov_positives") ?? r.live.positives,
      };
      return { ...r, params, used };
    });
    const usedMap = {};
    resolved.forEach((r) => { usedMap[r.key] = r.used; });
    const alloc = cppAllocate(ohDraft || [], usedMap);
    const wm = data.window_months || 1;
    const rows = resolved.map((r) => ({ ...r, calc: cppCalc(r.params, r.used.mailboxes, r.used.emails, r.used.positives, alloc[r.key], wm) }));
    const SUM = ["infra", "dataNeeded", "lead", "sequencer", "alloc", "overhead", "total",
                 "panelTerm", "domainTerm", "mbxTerm", "sourceSpend", "enrichSpend", "verifySpend", "other"];
    const t = {}; SUM.forEach((k) => { t[k] = 0; });
    t.mailboxes = 0; t.emails = 0; t.positives = 0;
    rows.forEach((r) => {
      t.mailboxes += Number(r.used.mailboxes) || 0;
      t.emails += Number(r.used.emails) || 0;
      t.positives += Number(r.used.positives) || 0;
      SUM.forEach((k) => { t[k] += r.calc[k]; });
    });
    t.panels = rows.reduce((a, r) => a + r.calc.panels, 0);
    t.domains = rows.reduce((a, r) => a + r.calc.domains, 0);
    t.cpp = t.positives ? t.total / t.positives : null;
    t.cppInfra = t.positives ? t.infra / t.positives : null;
    t.cppLead = t.positives ? t.lead / t.positives : null;
    t.cppOverhead = t.positives ? t.overhead / t.positives : null;
    t.perMailbox = t.mailboxes ? t.infra / t.mailboxes : null;
    t.epp = t.positives ? t.emails / t.positives : null;
    return { rows, totals: t };
  }, [data, edits, ohDraft]);

  const ohChanged = useMemo(() => {
    if (!data || !ohDraft) return false;
    const orig = (data.overheads || []).map((o) => `${o.id}|${o.label}|${o.amount}|${o.basis}`).join("~");
    const now = ohDraft.map((o) => `${o.id || ""}|${o.label}|${o.amount}|${o.basis}`).join("~");
    return orig !== now;
  }, [data, ohDraft]);
  const dirty = Object.keys(edits).some((rk) => Object.keys(edits[rk] || {}).length > 0) || ohChanged;

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      const params = {};
      Object.keys(edits).forEach((rk) => {
        params[rk] = {};
        Object.keys(edits[rk]).forEach((f) => {
          const raw = edits[rk][f];
          params[rk][f] = raw === "" || raw === null ? null : Number(raw);
        });
      });
      const body = { params };
      if (ohChanged) body.overheads = ohDraft.map((o) => ({ id: o.id, label: o.label, amount: Number(o.amount) || 0, basis: o.basis }));
      const r = await axios.put(`${API}/cpp`, body, authHeader(token));
      const errs = (r.data && r.data.errors) || [];
      setMsg(errs.length ? `Saved, ${errs.length} value(s) rejected: ${errs.join(", ")}` : "Saved.");
      load();
    } catch (e) { setMsg("Save failed."); } finally { setSaving(false); }
  };

  const resetRow = (rowKey) => {
    const blank = {};
    CPP_INFRA_COLS.concat(CPP_LEAD_COLS).forEach((c) => { blank[c.k] = ""; });
    ["avg_touchpoint", "avg_source_ratio", "sending_cost", "ov_mailboxes", "ov_emails", "ov_positives"].forEach((k) => { blank[k] = ""; });
    setEdits((prev) => ({ ...prev, [rowKey]: blank }));
  };

  const numCell = (r, key, step) => (
    <td key={key} className="cpp-edit">
      <input type="number" step={step} min="0" disabled={!isAdmin}
        title={String((edits[r.key] && Object.prototype.hasOwnProperty.call(edits[r.key], key)) ? edits[r.key][key] : r.params[key])}
        value={(edits[r.key] && Object.prototype.hasOwnProperty.call(edits[r.key], key)) ? edits[r.key][key] : r.params[key]}
        onChange={(ev) => setField(r.key, key, ev.target.value)} />
    </td>
  );

  const ovCell = (r, ovKey, liveVal) => {
    const edited = edits[r.key] && Object.prototype.hasOwnProperty.call(edits[r.key], ovKey);
    const stored = r.override[ovKey];
    const shown = edited ? edits[r.key][ovKey] : (stored === null || stored === undefined ? "" : stored);
    const overridden = shown !== "" && shown !== null && Number(shown) !== Number(liveVal);
    return (
      <td className={overridden ? "cpp-edit cpp-overridden" : "cpp-edit cpp-live"}>
        <input type="number" step="1" min="0" disabled={!isAdmin} placeholder={cppInt(liveVal)}
          value={shown} onChange={(ev) => setField(r.key, ovKey, ev.target.value)} />
        <em title={ovKey === "ov_mailboxes" && r.live.mailboxes_eb
          ? `${cppInt(r.live.mailboxes_pv)} on PlusVibe + ${cppInt(r.live.mailboxes_eb)} on EmailBison`
          : "Live value for the selected window"}>
          {cppInt(liveVal)}
          {ovKey === "ov_mailboxes" && r.live.mailboxes_eb ? ` (${cppInt(r.live.mailboxes_eb)} EB)` : ""}
        </em>
      </td>
    );
  };

  const th = (label, formula, cls) => (
    <th className={cls}>{label}{formula && <span className="cpp-f">{formula}</span>}</th>
  );

  const t = view ? view.totals : null;
  const ohTotal = (ohDraft || []).reduce((s, o) => s + (Number(o.amount) || 0), 0);

  // ---- non-technical view. Per-positive figures only: no total spend, no overhead line
  // amounts, no per-row cost model. Reads /cpp/summary, same computation as the admin tab.
  if (!isAdmin) {
    const money = (v) => (v === null || v === undefined ? "—" : `$${Number(v).toFixed(2)}`);
    const whole = (v) => (v === null || v === undefined ? "—" : Math.round(Number(v)).toLocaleString());
    return (
      <section className="score-table-panel cpp-panel">
        <div className="targets-topbar">
          <div>
            <h3>Cost per positive</h3>
            <p className="targets-sub">
              What it costs us on average to produce one interested reply, over the last{" "}
              {data ? data.effective_days : days} days. The three parts always add up to the total.
            </p>
          </div>
          <div className="targets-actions">
            <div className="cpp-daypills" role="group" aria-label="Window">
              {[7, 14, 30].map((d) => (
                <button key={d} type="button"
                  className={days === d ? "cpp-daypill active" : "cpp-daypill"}
                  onClick={() => setDays(d)}>{d}D</button>
              ))}
            </div>
            <button className="btn-small" type="button" onClick={load} disabled={loading}>
              {loading ? "Loading…" : "Refresh"}
            </button>
          </div>
        </div>

        {msg && <p className="targets-sub">{msg}</p>}

        <div className="cpp-headline">
          <div className="cpp-eq">
            <div className="cpp-eq-part">
              <span className="cpp-eq-label">Infrastructure</span>
              <strong>{money(data && data.cpp_infra)}</strong>
              <em>the mailboxes, domains and accounts we send from</em>
            </div>
            <span className="cpp-eq-op">+</span>
            <div className="cpp-eq-part">
              <span className="cpp-eq-label">Lead data</span>
              <strong>{money(data && data.cpp_lead)}</strong>
              <em>buying, enriching and verifying contacts</em>
            </div>
            <span className="cpp-eq-op">+</span>
            <div className="cpp-eq-part">
              <span className="cpp-eq-label">Running costs</span>
              <strong>{money(data && data.cpp_overhead)}</strong>
              <em>sending platform and management</em>
            </div>
            <span className="cpp-eq-op">=</span>
            <div className="cpp-eq-part cpp-eq-result">
              <span className="cpp-eq-label">Cost per positive</span>
              <strong>{money(data && data.cpp)}</strong>
              <em>{data ? `${whole(data.positives)} positives · ${whole(data.emails_per_positive)} emails each` : ""}</em>
            </div>
          </div>
        </div>

        {data && Array.isArray(data.excludes) && data.excludes.length > 0 && (
          <p className="targets-sub">
            <strong>Not included:</strong> {data.excludes.join(", ")}. These are set to zero in the
            cost model, so the figure above is lower than our true all-in cost.
          </p>
        )}

        <div className="definitions-grid">
          <div className="definition-card">
            <strong>Positive</strong>
            <span>An interested reply from a prospect. The outcome all of this cost is paying for.</span>
          </div>
          <div className="definition-card">
            <strong>Infrastructure</strong>
            <span>Mailboxes, domains and email accounts, shared across the positives they produced.</span>
          </div>
          <div className="definition-card">
            <strong>Lead data</strong>
            <span>What we pay to find a contact, fill in their details and check the address is real.</span>
          </div>
          <div className="definition-card">
            <strong>Running costs</strong>
            <span>The sending platform plus management time, spread across all sending.</span>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="score-table-panel cpp-panel">
      <div className="targets-topbar">
        <div>
          <h3>Real cost per positive</h3>
          <p className="targets-sub">
            Mailboxes count PlusVibe plus the EmailBison fleet still on Baton.
            Emails sent and positives are live for the last {data ? data.window_days : days} days per infra tag
            {data && data.excluded_workspaces ? ", Ani's Workspace excluded" : ""}. Mailboxes are a current snapshot, not a window.
            Type into any volume cell to override it, clear it to go back to live.
            {data && data.effective_days !== 30 && (
              <strong> Infra and fixed overheads are monthly, so they are pro-rated to {data.effective_days} days
                ({(data.window_months || 1).toFixed(3)} of a month) to match the volumes.</strong>
            )}
            {data && data.window_clamped && (
              <strong> Only {data.available_days} days of event history exist (events are pruned at 35 days),
                so the window was clamped to that.</strong>
            )}
          </p>
        </div>
        <div className="targets-actions">
          {dirty && <span className="cpp-dirty">Unsaved changes</span>}
          {!dirty && msg && <span className="targets-saved">{msg}</span>}
          <label className="cpp-toggle" title="Show the component cost of each bucket, not just its total">
            <input type="checkbox" checked={showSub} onChange={(e) => setShowSub(e.target.checked)} /> Show sub-costs
          </label>
          <div className="cpp-daypills" role="group" aria-label="Window">
            {[7, 14, 30].map((d) => (
              <button key={d} type="button"
                className={days === d ? "cpp-daypill active" : "cpp-daypill"}
                onClick={() => setDays(d)}>{d}D</button>
            ))}
          </div>
          <button className="btn-small" type="button" onClick={load} disabled={loading}>{loading ? "Loading…" : "Refresh"}</button>
          {isAdmin
            ? <button className="btn-small btn-primary" type="button" disabled={saving || !dirty} onClick={save}>{saving ? "Saving…" : "Save"}</button>
            : <span className="targets-readonly">Read-only. Sign in as admin to edit.</span>}
        </div>
      </div>

      {/* the headline: CPP decomposed into its three buckets */}
      <div className="cpp-headline">
        <div className="cpp-eq">
          <div className="cpp-eq-part">
            <span className="cpp-eq-label">Infra / positive</span>
            <strong>{t ? cppMoney(t.cppInfra) : "—"}</strong>
            <em>tenants + domains + mailboxes</em>
          </div>
          <span className="cpp-eq-op">+</span>
          <div className="cpp-eq-part">
            <span className="cpp-eq-label">Lead / positive</span>
            <strong>{t ? cppMoney(t.cppLead) : "—"}</strong>
            <em>source + enrichment + verification</em>
          </div>
          <span className="cpp-eq-op">+</span>
          <div className="cpp-eq-part">
            <span className="cpp-eq-label">Overhead / positive</span>
            <strong>{t ? cppMoney(t.cppOverhead) : "—"}</strong>
            <em>sequencer + fixed overheads</em>
          </div>
          <span className="cpp-eq-op">=</span>
          <div className="cpp-eq-part cpp-eq-result">
            <span className="cpp-eq-label">Real CPP</span>
            <strong>{t ? cppMoney(t.cpp) : "—"}</strong>
            <em>{t ? `${cppInt(t.positives)} positives · ${cppMoney(t.total)} total cost` : ""}</em>
          </div>
        </div>
      </div>

      {/* ---- overheads ---- */}
      <div className="cpp-oh">
        <div className="cpp-oh-head">
          <div>
            <h3>Fixed monthly overheads</h3>
            <p className="targets-sub">
              Costs that are not per-mailbox and not per-lead: people, tools, management. Each line is spread
              across the sending types on the basis you pick, so it lands in CPP instead of being ignored.
              A basis with no volume falls back to an equal split rather than dropping the cost.
              <strong> Edits here only stick once you press Save.</strong>
            </p>
          </div>
          {isAdmin && (
            <div className="cpp-oh-save">
              {dirty && <span className="cpp-dirty">Unsaved changes</span>}
              <button className="btn-small btn-primary" type="button" disabled={saving || !dirty} onClick={save}>
                {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
              </button>
            </div>
          )}
        </div>
        <table className="cpp-ohtable">
          <thead>
            <tr><th>Line item</th><th>Amount / month</th><th>Allocate by</th><th /></tr>
          </thead>
          <tbody>
            {(ohDraft || []).map((o, i) => (
              <tr key={o.id || `new-${i}`}>
                <td><input type="text" disabled={!isAdmin} value={o.label}
                  onChange={(e) => setOhDraft((p) => p.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} /></td>
                <td><input type="number" step="0.01" min="0" disabled={!isAdmin} value={o.amount}
                  onChange={(e) => setOhDraft((p) => p.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))} /></td>
                <td>
                  <select disabled={!isAdmin} value={o.basis}
                    onChange={(e) => setOhDraft((p) => p.map((x, j) => (j === i ? { ...x, basis: e.target.value } : x)))}>
                    {(data ? data.overhead_bases : ["emails"]).map((b) => <option key={b} value={b}>{CPP_BASIS_LABEL[b] || b}</option>)}
                  </select>
                </td>
                <td>{isAdmin && <button className="btn-small" type="button" onClick={() => setOhDraft((p) => p.filter((_, j) => j !== i))}>Remove</button>}</td>
              </tr>
            ))}
            <tr className="cpp-oh-total">
              <th>Total fixed overheads</th>
              <td>
                {cppMoney(ohTotal)}
                {data && data.effective_days !== 30 && (
                  <em className="cpp-oh-pro">{cppMoney(ohTotal * (data.window_months || 1))} over {data.effective_days}d</em>
                )}
              </td>
              <td colSpan={2}>{isAdmin && <button className="btn-small" type="button" onClick={() => setOhDraft((p) => [...(p || []), { label: "New overhead", amount: 0, basis: "emails" }])}>+ Add line item</button>}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {view && view.rows.some((r) => (Number(r.used.mailboxes) || 0) === 0 && (Number(r.used.emails) || 0) > 0) && (
        <div className="cpp-warn">
          <AlertTriangle size={15} />
          <span>
            A row has sending volume but zero mailboxes, so its infra cost reads 0 and its CPP is understated.
            Expected for infra decommissioned inside the window (EmailBison went Baton-only on 31 Jul 2026).
            Override the mailbox count to model it properly.
          </span>
        </div>
      )}

      <div className="cpp-scroll">
        <table className="cpp-table">
          <thead>
            <tr className="cpp-grouprow">
              <th />
              <th colSpan={3} className="g-vol">Volume (live)</th>
              <th colSpan={showSub ? 14 : 11} className="g-infra">1 · Infra cost</th>
              <th colSpan={showSub ? 10 : 7} className="g-lead">2 · Lead cost</th>
              <th colSpan={4} className="g-oh">3 · Overheads</th>
              <th colSpan={showSub ? 6 : 5} className="g-res">Real CPP</th>
              <th />
            </tr>
            <tr>
              <th className="cpp-sticky">Type</th>
              {th("Mailboxes", "live / override")}
              {th("Emails sent", "live / override")}
              {th("Positives", "live / override")}
              {CPP_INFRA_COLS.map((c) => <th key={c.k}>{c.h}<span className="cpp-f">{c.sub}</span></th>)}
              {th("Panels", "Mbx ÷ (Dom/Panel × Mbx/Dom)", "cpp-calc-h")}
              {th("Domains", "Mbx ÷ Mbx/Dom", "cpp-calc-h")}
              {showSub && th("Panel spend", "Panels × (Panel ÷ Period)", "cpp-calc-h")}
              {showSub && th("Domain spend", "Domains × (Domain ÷ Period)", "cpp-calc-h")}
              {showSub && th("Mailbox spend", "Mbx × (Mailbox ÷ Period)", "cpp-calc-h")}
              {th("Infra cost", "Panel + Domain + Mailbox spend", "cpp-calc-h cpp-sub-h")}
              {th("Avg touchpoints", "per lead")}
              {th("Source ratio", "usable share")}
              {th("Data needed", "Emails ÷ Touchpoints ÷ Ratio", "cpp-calc-h")}
              {CPP_LEAD_COLS.map((c) => <th key={c.k}>{c.h}<span className="cpp-f">{c.sub}</span></th>)}
              {showSub && th("Source spend", "Data needed × Source", "cpp-calc-h")}
              {showSub && th("Enrichment spend", "Data needed × Enrichment", "cpp-calc-h")}
              {showSub && th("Verification spend", "Data needed × Verification", "cpp-calc-h")}
              {th("Lead cost", "Source + Enrichment + Verification spend", "cpp-calc-h cpp-sub-h")}
              {th("Sending cost", "$ per email")}
              {th("Sequencer", "Sending cost × Emails", "cpp-calc-h")}
              {th("Allocated", "share of fixed overheads", "cpp-calc-h")}
              {th("Overheads", "Sequencer + Allocated", "cpp-calc-h cpp-sub-h")}
              {showSub && th("Total other cost", "Lead + Overheads  ·  sheet col W", "cpp-calc-h")}
              {th("Total cost", "Infra + Lead + Overheads", "cpp-calc-h")}
              {th("Infra / pos", "Infra ÷ Positives", "cpp-calc-h")}
              {th("Lead / pos", "Lead ÷ Positives", "cpp-calc-h")}
              {th("OH / pos", "Overheads ÷ Positives", "cpp-calc-h")}
              {th("CPP", "Total ÷ Positives", "cpp-calc-h cpp-hero-h")}
              <th />
            </tr>
          </thead>
          <tbody>
            {view && view.rows.map((r) => (
              <tr key={r.key}>
                <th className="cpp-sticky" title={`Infra tags: ${r.tags.join(", ")}`}>
                  {r.label}<em>{r.tags.join(", ")}</em>
                </th>
                {ovCell(r, "ov_mailboxes", r.live.mailboxes)}
                {ovCell(r, "ov_emails", r.live.emails)}
                {ovCell(r, "ov_positives", r.live.positives)}
                {CPP_INFRA_COLS.map((c) => numCell(r, c.k, c.step))}
                <td className="cpp-calc">{cppFmt(r.calc.panels, 1)}</td>
                <td className="cpp-calc">{cppFmt(r.calc.domains, 1)}</td>
                {showSub && <td className="cpp-calc">{cppMoney(r.calc.panelTerm)}</td>}
                {showSub && <td className="cpp-calc">{cppMoney(r.calc.domainTerm)}</td>}
                {showSub && <td className="cpp-calc">{cppMoney(r.calc.mbxTerm)}</td>}
                <td className="cpp-calc cpp-sub">{cppMoney(r.calc.infra)}</td>
                {numCell(r, "avg_touchpoint", "0.1")}
                {numCell(r, "avg_source_ratio", "0.01")}
                <td className="cpp-calc">{cppInt(r.calc.dataNeeded)}</td>
                {CPP_LEAD_COLS.map((c) => numCell(r, c.k, c.step))}
                {showSub && <td className="cpp-calc">{cppMoney(r.calc.sourceSpend)}</td>}
                {showSub && <td className="cpp-calc">{cppMoney(r.calc.enrichSpend)}</td>}
                {showSub && <td className="cpp-calc">{cppMoney(r.calc.verifySpend)}</td>}
                <td className="cpp-calc cpp-sub">{cppMoney(r.calc.lead)}</td>
                {numCell(r, "sending_cost", "0.0001")}
                <td className="cpp-calc">{cppMoney(r.calc.sequencer)}</td>
                <td className="cpp-calc">{cppMoney(r.calc.alloc)}</td>
                <td className="cpp-calc cpp-sub">{cppMoney(r.calc.overhead)}</td>
                {showSub && <td className="cpp-calc">{cppMoney(r.calc.other)}</td>}
                <td className="cpp-calc">{cppMoney(r.calc.total)}</td>
                <td className="cpp-calc">{cppMoney(r.calc.cppInfra)}</td>
                <td className="cpp-calc">{cppMoney(r.calc.cppLead)}</td>
                <td className="cpp-calc">{cppMoney(r.calc.cppOverhead)}</td>
                <td className="cpp-calc cpp-hero">{cppMoney(r.calc.cpp)}</td>
                <td className="cpp-edit">{isAdmin && <button className="btn-small" type="button" title="Clear stored values for this row" onClick={() => resetRow(r.key)}>Reset</button>}</td>
              </tr>
            ))}
            {view && (
              <tr className="cpp-total">
                <th className="cpp-sticky">Blended</th>
                <td>{cppInt(t.mailboxes)}</td>
                <td>{cppInt(t.emails)}</td>
                <td>{cppInt(t.positives)}</td>
                <td colSpan={8} />
                <td className="cpp-calc">{cppFmt(t.panels, 1)}</td>
                <td className="cpp-calc">{cppFmt(t.domains, 1)}</td>
                {showSub && <td className="cpp-calc">{cppMoney(t.panelTerm)}</td>}
                {showSub && <td className="cpp-calc">{cppMoney(t.domainTerm)}</td>}
                {showSub && <td className="cpp-calc">{cppMoney(t.mbxTerm)}</td>}
                <td className="cpp-calc cpp-sub">{cppMoney(t.infra)}</td>
                <td colSpan={2} />
                <td className="cpp-calc">{cppInt(t.dataNeeded)}</td>
                <td colSpan={3} />
                {showSub && <td className="cpp-calc">{cppMoney(t.sourceSpend)}</td>}
                {showSub && <td className="cpp-calc">{cppMoney(t.enrichSpend)}</td>}
                {showSub && <td className="cpp-calc">{cppMoney(t.verifySpend)}</td>}
                <td className="cpp-calc cpp-sub">{cppMoney(t.lead)}</td>
                <td />
                <td className="cpp-calc">{cppMoney(t.sequencer)}</td>
                <td className="cpp-calc">{cppMoney(t.alloc)}</td>
                <td className="cpp-calc cpp-sub">{cppMoney(t.overhead)}</td>
                {showSub && <td className="cpp-calc">{cppMoney(t.other)}</td>}
                <td className="cpp-calc">{cppMoney(t.total)}</td>
                <td className="cpp-calc">{cppMoney(t.cppInfra)}</td>
                <td className="cpp-calc">{cppMoney(t.cppLead)}</td>
                <td className="cpp-calc">{cppMoney(t.cppOverhead)}</td>
                <td className="cpp-calc cpp-hero">{cppMoney(t.cpp)}</td>
                <td />
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="cpp-side">
        <div>
          <span className="cpp-side-k">Infra cost / mailbox</span>
          <strong>{t ? cppMoney(t.perMailbox, 3) : "—"}</strong>
          <em>Infra ÷ Mailboxes</em>
        </div>
        <div>
          <span className="cpp-side-k">Emails per positive</span>
          <strong>{t ? cppInt(t.epp) : "—"}</strong>
          <em>Emails ÷ Positives</em>
        </div>
        <div>
          <span className="cpp-side-k">Cost per email</span>
          <strong>{t && t.emails ? cppMoney(t.total / t.emails, 4) : "—"}</strong>
          <em>Total ÷ Emails</em>
        </div>
        <div>
          <span className="cpp-side-k">Fixed overheads</span>
          <strong>{cppMoney(ohTotal)}</strong>
          <em>per month, allocated across rows</em>
        </div>
      </div>

      {data && data.unmapped_tags && Object.keys(data.unmapped_tags).length > 0 && (
        <p className="cpp-note">
          Not counted in any row, infra tag unmapped:{" "}
          {Object.keys(data.unmapped_tags).map((tg) => `${tg} (${cppInt(data.unmapped_tags[tg].emails)} emails, ${cppInt(data.unmapped_tags[tg].positives)} positives)`).join("; ")}
        </p>
      )}

      <h3 className="definition-heading">Formulas</h3>
      <div className="definition-grid">
        <div className="definition-card"><strong>Panels needed</strong><code>Mailboxes ÷ (Domains per panel × Mailboxes per domain)</code><span>How many tenants or panels that mailbox count actually requires.</span></div>
        <div className="definition-card"><strong>Domains needed</strong><code>Mailboxes ÷ Mailboxes per domain</code><span>How many sending domains that mailbox count requires.</span></div>
        <div className="definition-card"><strong>1 · Infra cost</strong><code>Panels × (Panel ÷ Period) + Domains × (Domain ÷ Period) + Mailboxes × (Mailbox ÷ Period)</code><span>Each component divided by its own billing period so everything lands on a monthly basis.</span></div>
        <div className="definition-card"><strong>Data needed</strong><code>Emails sent ÷ Avg touchpoints ÷ Source ratio</code><span>Leads required to support the send volume, grossed up for the share of sourced records that turn out usable.</span></div>
        <div className="definition-card"><strong>2 · Lead cost</strong><code>Data needed × (Source + Enrichment + Verification)</code><span>What the data costs before a single email goes out.</span></div>
        <div className="definition-card"><strong>Infra sub-costs</strong><code>Panel spend + Domain spend + Mailbox spend = Infra cost</code><span>The three terms of the sheet's Infra Cost formula, shown separately so you can see whether tenants, domains or per-mailbox fees dominate.</span></div>
        <div className="definition-card"><strong>Lead sub-costs</strong><code>Source spend + Enrichment spend + Verification spend = Lead cost</code><span>Each is Data needed multiplied by its own unit rate. Together they are the sheet's Total Working Cost.</span></div>
        <div className="definition-card"><strong>Total other cost</strong><code>Lead cost + Overheads</code><span>Everything that is not infrastructure. This is the sheet's column W, kept so the two tie out.</span></div>
        <div className="definition-card"><strong>Sequencer</strong><code>Sending cost × Emails sent</code><span>Per-email platform charge.</span></div>
        <div className="definition-card"><strong>Allocated overhead</strong><code>Line item × (row basis ÷ total basis)</code><span>Each fixed monthly overhead spread across the sending types on its own basis. Equal split when the basis has no volume.</span></div>
        <div className="definition-card"><strong>3 · Overheads</strong><code>Sequencer + Allocated overhead</code><span>Everything that is neither infrastructure nor data.</span></div>
        <div className="definition-card"><strong>Total cost</strong><code>Infra cost + Lead cost + Overheads</code><span>Fully loaded monthly cost for that sending type.</span></div>
        <div className="definition-card cpp-card-hero"><strong>Real CPP</strong><code>Total cost ÷ Positives = (Infra ÷ Pos) + (Lead ÷ Pos) + (Overheads ÷ Pos)</code><span>The number this tab exists for. The three per-positive columns always add up to it, so you can see which bucket is driving the cost.</span></div>
        <div className="definition-card"><strong>Infra cost / mailbox</strong><code>Infra cost ÷ Mailboxes</code><span>Infra only, so sending types compare like for like regardless of volume.</span></div>
        <div className="definition-card"><strong>Emails per positive</strong><code>Emails sent ÷ Positives</code><span>The volume side of CPP. Separates a cost problem from a conversion problem.</span></div>
      </div>
    </section>
  );
}

function DefinitionsTab({ token, isAdmin }) {
  const metricDefinitions = [
    {
      term: "Any reply rate",
      formula: "Unique replying leads / unique people contacted",
      notes: "Matches the EmailBison dashboard style. OOO replies are included, but repeat replies from the same lead are counted once.",
    },
    {
      term: "Human RR",
      formula: "Unique non-OOO replying leads / unique people contacted",
      notes: "Uses reply webhooks where automated_reply is false. This is the cleanest signal for real human engagement.",
    },
    {
      term: "OOO RR",
      formula: "Unique OOO replying leads / unique people contacted",
      notes: "Uses reply webhooks where automated_reply is true. Useful for deliverability and inbox activity, but not a human intent signal.",
    },
    {
      term: "Reply events",
      formula: "Raw reply webhook count",
      notes: "Counts every tracked reply event. This can be higher than unique repliers when a lead replies more than once.",
    },
    {
      term: "Event reply rate",
      formula: "Reply events / sent emails",
      notes: "Kept in the backend for audit/debugging. It will usually not match EmailBison's reply-rate card.",
    },
    {
      term: "Bounce rate",
      formula: "Webhook bounces / sent emails",
      notes: "Includes the bounce events captured by webhook for the selected period and workspace filter.",
    },
    {
      term: "EPP",
      formula: "Sent emails / positive leads",
      notes: "Emails per positive. Lower is better. Positive leads come from webhook positive lead events.",
    },
    {
      term: "CPP",
      formula: "Loaded infra cost / positive leads",
      notes: "Blended cost per positive. Cost uses configured own-infra and third-party infra cost models.",
    },
    {
      term: "People contacted",
      formula: "Distinct lead ID, lead UUID, or lead email from sent events",
      notes: "This is the denominator for EmailBison-style reply rate.",
    },
    {
      term: "Exclude Baton",
      formula: "All selected-period stats minus Baton Market workspaces",
      notes: "Useful for seeing portfolio health without the largest client dominating the totals.",
    },
  ];

  const statusDefinitions = [
    {
      term: "Replace",
      formula: "Strong reputation-risk evidence",
      notes: "Domain Intelligence flags a domain for replacement when complaint/spam-style bounces are high, persistent, and spread across recipient domains.",
    },
    {
      term: "Watch",
      formula: "Moderate or developing risk",
      notes: "The domain has enough signal to monitor closely, but the evidence is not yet strong enough for replacement.",
    },
    {
      term: "Learning",
      formula: "Insufficient history",
      notes: "The app has seen activity, but not enough stable evidence to make a confident call.",
    },
    {
      term: "Healthy",
      formula: "No immediate action signal",
      notes: "Current bounce and reply patterns do not show an active replacement/watch trigger.",
    },
  ];

  const [targets, setTargets] = useState([]);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");

  const fetchTargets = () => {
    axios.get(`${API}/targets`, authHeader(token)).then((r) => {
      const defs = (r.data && r.data.targets) || [];
      setTargets(defs);
      const v = {};
      defs.forEach((d) => { v[d.key] = d.value; });
      setValues(v);
    }).catch(() => {});
  };
  useEffect(() => { fetchTargets(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [token]);

  const sectionOrder = [];
  const sections = {};
  targets.forEach((d) => {
    const secName = d.section || "Targets";
    if (!sections[secName]) { sections[secName] = { order: [], groups: {} }; sectionOrder.push(secName); }
    const sec = sections[secName];
    if (!sec.groups[d.group]) { sec.groups[d.group] = []; sec.order.push(d.group); }
    sec.groups[d.group].push(d);
  });
  const dirty = targets.some((d) => Number(values[d.key]) !== Number(d.value));

  const saveTargets = async () => {
    setSaving(true); setSavedMsg("");
    try {
      const payload = {};
      targets.forEach((d) => { payload[d.key] = Number(values[d.key]); });
      await axios.put(`${API}/targets`, { targets: payload }, authHeader(token));
      setSavedMsg("Saved. New thresholds are live across the dashboard.");
      fetchTargets();
    } catch (e) {
      setSavedMsg("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = () => {
    setValues((prev) => {
      const v = { ...prev };
      targets.forEach((d) => { v[d.key] = d.default; });
      return v;
    });
  };

  return (
    <section className="score-table-panel definitions-panel">
      <div className="section-title compact">
        <Database size={18} />
        <div>
          <h2>Targets and Definitions</h2>
          <p>Editable thresholds that drive the Infra Health status pills, plus the metric calculation reference.</p>
        </div>
      </div>

      <div className="targets-topbar">
        <div>
          <h3>Dashboard targets</h3>
          <p className="targets-sub">These cutoffs drive the Infra Health status pills, the domain-removal retire bars, and the bounce noise-gates. Changes apply live. Editing requires admin sign-in.</p>
        </div>
        {isAdmin ? (
          <div className="targets-actions">
            {savedMsg && <span className="targets-saved">{savedMsg}</span>}
            <button className="btn-small" type="button" onClick={resetDefaults}>Reset to defaults</button>
            <button className="btn-small btn-primary" type="button" disabled={saving || !dirty} onClick={saveTargets}>{saving ? "Saving…" : "Save targets"}</button>
          </div>
        ) : (
          <span className="targets-readonly">Read-only. Sign in as admin to edit.</span>
        )}
      </div>
      {sectionOrder.map((secName) => (
        <div className="targets-block" key={secName}>
          <div className="targets-section-name">{secName}</div>
          {sections[secName].order.map((g) => (
            <div className="target-group" key={g}>
              <div className="target-group-name">{g}</div>
              <div className="target-grid">
                {sections[secName].groups[g].map((d) => (
                  <label className="target-field" key={d.key}>
                    <span className="target-label">{d.label}</span>
                    <span className="target-input">
                      <input
                        type="number"
                        step={d.kind === "int" ? "1" : "0.1"}
                        min="0"
                        value={values[d.key] ?? ""}
                        disabled={!isAdmin}
                        onChange={(e) => setValues((prev) => ({ ...prev, [d.key]: e.target.value }))}
                      />
                      <em>{d.unit}</em>
                    </span>
                    <span className="target-help">{d.help}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      ))}

      <h3 className="definition-heading">Metric definitions</h3>
      <div className="definition-grid">
        {metricDefinitions.map((item) => (
          <div className="definition-card" key={item.term}>
            <strong>{item.term}</strong>
            <code>{item.formula}</code>
            <span>{item.notes}</span>
          </div>
        ))}
      </div>
      <div className="definition-section">
        <h3>Domain AI statuses</h3>
        <div className="definition-grid compact">
          {statusDefinitions.map((item) => (
            <div className="definition-card" key={item.term}>
              <strong>{item.term}</strong>
              <code>{item.formula}</code>
              <span>{item.notes}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const dataTableLabels = {
  sent_events: "Email Sent",
  positive_lead_events: "Positive Leads",
  bounce_events: "Bounces",
  reply_events: "Replies",
};

const dataColumnLabels = {
  id: "ID",
  occurred_at: "Occurred",
  workspace_name: "Workspace",
  infra_group: "Group",
  infra_tag: "Tag",
  sender_email: "Sender",
  lead_email: "Lead",
  campaign_name: "Campaign",
  subject: "Subject",
  scheduled_email_id: "Scheduled ID",
  reply_id: "Reply ID",
  campaign_event_id: "Event ID",
  recipient_domain: "Recipient Domain",
  bounce_category: "Bounce Type",
  smtp_status_code: "SMTP",
  from_email: "From",
  automated_reply: "OOO",
  interested: "Interested",
};

function dataCellValue(column, value) {
  if (value === null || value === undefined || value === "") return "-";
  if (column === "occurred_at") return fmtDate(value);
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function DataTablesPanel({ data, selectedTable, onSelectTable }) {
  const tables = data?.tables || [];
  const rows = data?.rows || [];
  const columns = data?.columns || [];
  return (
    <section className="data-page">
      <div className="section-title compact command-title">
        <Database size={18} />
        <div>
          <h2>Data Tables</h2>
          <p>Read-only event store for infra performance calculations.</p>
        </div>
      </div>
      <div className="data-table-tabs">
        {tables.map((item) => (
          <button
            key={item.key}
            className={selectedTable === item.key ? "data-tab active" : "data-tab"}
            onClick={() => onSelectTable(item.key)}
          >
            <span>{item.label || dataTableLabels[item.key] || item.key}</span>
            <strong>{numberFmt(item.count)}</strong>
          </button>
        ))}
      </div>
      <section className="score-table-panel">
        <div className="section-title compact table-title-row">
          <div>
            <h2>{data?.label || dataTableLabels[selectedTable] || selectedTable}</h2>
            <p>Latest 50 rows, newest first. Raw payloads are stored but hidden here.</p>
          </div>
        </div>
        <div className="score-table-wrap data-table-wrap">
          <table className="score-table data-table">
            <thead>
              <tr>
                {columns.map((column) => <th key={column}>{dataColumnLabels[column] || column}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${selectedTable}-${row.id}`}>
                  {columns.map((column) => (
                    <td key={column} title={dataCellValue(column, row[column])}>{dataCellValue(column, row[column])}</td>
                  ))}
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={columns.length || 1} className="table-empty">No rows stored for this table yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </section>
  );
}

function L10ScorecardPage({
  section,
  setSection,
  data,
  positives,
  bounces,
  health,
  domains,
  trends,
  period,
  periodLoading,
  workspaceFilter,
  workspaceOptions,
  excludeBaton,
  platform,
  dateFilter,
  filterKey,
  onPeriodChange,
  onWorkspaceChange,
  onExcludeBatonChange,
  onPlatformChange,
  onDateFilterApply,
  onDateFilterClear,
  onFetchEvents,
  onFetchDomains,
  onFetchTrends,
  onFetchCampaignBounces,
  campaignBounces,
  onExecuteDomain,
  executingDomain,
  onRun,
  running,
  token,
  isAdmin,
}) {
  const [draftStart, setDraftStart] = useState(dateFilter?.startDate || "");
  const [draftEnd, setDraftEnd] = useState(dateFilter?.endDate || "");
  const latest = data?.latest;
  const workspaces = data?.workspaces || [];
  const tagSummary = (data?.tag_summary || []).filter((row) => row.infra_group !== "unclassified");
  const tagDetails = (data?.tag_details || []).filter((row) => row.infra_group !== "unclassified");
  const history = data?.history || [];
  const healthTotals = health?.totals || {};
  const bounceCount = healthTotals.workspace_bounce_risk_count ?? latest?.bounce_risk_workspace_count ?? 0;
  const replyRate = Number(healthTotals.reply_rate ?? latest?.reply_rate ?? 0);
  const uniqueReplyCount = Number(healthTotals.unique_replies ?? healthTotals.webhook_replies ?? 0);
  const replyEventCount = Number(healthTotals.webhook_replies ?? 0);
  const epp = Number(health?.totals?.epp || 0);
  const bounceRiskWorkspaces = (health?.workspaces || [])
    .filter((row) => Number(row.sent || row.sent_delta || 0) >= 500 && Number(row.bounce_rate || 0) > 2)
    .sort((a, b) => Number(b.bounce_rate || 0) - Number(a.bounce_rate || 0));
  const rollingLabels = { "1d": "Rolling 1 day", "7d": "Rolling 7 days", "14d": "Rolling 14 days", "30d": "Rolling 30 days", "6m": "Rolling 6 months", "mtd": "Month to date", "today": "Today", "yesterday": "Yesterday" };
  const basisLabel = (dateFilter?.startDate && dateFilter?.endDate)
    ? `${dateFilter.startDate} to ${dateFilter.endDate}`
    : (rollingLabels[period] || `Rolling ${periodLabel(period)}`);
  const bounceStatus = bounceCount >= 3 ? "red" : bounceCount > 0 ? "watch" : "ok";
  const replyStatus = replyRate <= 2 ? "red" : replyRate < 3 ? "watch" : "ok";
  const attnIssues = (health?.pv_inventory?.by_tag || []).reduce((sum, t) => sum + (Number(t.issues) || 0), 0);
  const attnItems = [];
  if (bounceCount > 0) attnItems.push({ key: "bounce", tone: bounceStatus === "red" ? "red" : "warn", text: bounceCount + " workspace" + (bounceCount > 1 ? "s" : "") + " over 2% bounce", go: "campaign-bounces" });
  if (attnIssues > 0) attnItems.push({ key: "issues", tone: "warn", text: numberFmt(attnIssues) + " mailboxes need attention", go: "details" });
  if (replyStatus === "red") attnItems.push({ key: "reply", tone: "red", text: "Reply rate " + replyRate.toFixed(2) + "% below target", go: "trends" });
  const trendDaysAll = dailyTotals(trends?.buckets);
  // A day still in progress (and the first partial day of history) has almost no sends
  // but still collects replies and bounces for mail sent earlier, which yields absurd
  // rates: today read 409% reply and 43% bounce off 21 sends. Sparkline scales to
  // min/max, so a single point like that squashes the real 1.5-5.8% range into a flat
  // line. Drop buckets whose volume is a small fraction of the norm.
  const trendDays = (() => {
    const sents = trendDaysAll.map((d) => Number(d.sent) || 0).filter((v) => v > 0).sort((a, b) => a - b);
    if (sents.length < 3) return trendDaysAll;
    const median = sents[Math.floor(sents.length / 2)];
    const floor = median * 0.2;
    const kept = trendDaysAll.filter((d) => (Number(d.sent) || 0) >= floor);
    return kept.length >= 2 ? kept : trendDaysAll;
  })();
  const sparkReply = trendDays.map((d) => (d.sent ? (d.replies / d.sent) * 100 : 0));
  const sparkBounce = trendDays.map((d) => (d.sent ? (d.bounces / d.sent) * 100 : 0));
  const sparkPositives = trendDays.map((d) => d.positives);
  const replyDelta = halfRateDelta(trendDays, "replies");
  const bounceDelta = halfRateDelta(trendDays, "bounces");
  const positivesDelta = halfCountDelta(trendDays, "positives");
  const hasCustomDates = Boolean(dateFilter?.startDate && dateFilter?.endDate);
  const activePeriodLabel = hasCustomDates ? `${dateFilter.startDate} to ${dateFilter.endDate}` : periodLabel(period);

  useEffect(() => {
    setDraftStart(dateFilter?.startDate || "");
    setDraftEnd(dateFilter?.endDate || "");
  }, [dateFilter?.startDate, dateFilter?.endDate]);

  useEffect(() => {
    // leaving a rollup-capable section while on 6M would send 6m to an endpoint that
    // silently truncates to raw retention, so fall back to 30d
    if (period === "6m" && !["summary", "details", "trends"].includes(section)) {
      onPeriodChange("30d");
      return;
    }
    if (section === "events") onFetchEvents(period);
    if (section === "domains") onFetchDomains(period);
    if (section === "trends" || section === "summary") onFetchTrends(period);
    if (section === "campaign-bounces") onFetchCampaignBounces(period);
  }, [section, period, filterKey, onFetchEvents, onFetchDomains, onFetchTrends, onFetchCampaignBounces]);

  return (
    <section className="scorecard-page">
      <div className="scorecard-hero">
        <div className="sc-hero-main">
          <h1>Infra performance</h1>
          <p className="sc-meta">{latest ? "Last calculated " + fmtDate(latest.started_at) + " \u00b7 " + basisLabel : "No run yet \u00b7 run the monitor to create the first snapshot"}</p>
        </div>
        {health && health.partial_window && (
          <div className="sc-partial-note">
            <AlertTriangle size={14} />
            <span>
              {periodLabel(period)} selected but only <strong>{health.days_available} of {health.days_requested} days</strong> of
              history exist (from {String(health.history_starts || "").slice(0, 10)}). Aggregates are built from
              the daily rollup, so unique counts are approximate to about 1%
              {health.bounce_reasons_available === false ? ", and the bounce-reason breakdown is unavailable past 35 days" : ""}.
            </span>
          </div>
        )}
        <div className="sc-hero-controls">
          {periodLoading && <span className="period-loading"><RefreshCw size={13} /> Updating {activePeriodLabel}...</span>}
          <div className="period-switch" aria-label="Calculation period">
            {/* 6M only where the rollup can answer it. Positive Leads, Workspace Bounces
                and Domains still read raw events, which are pruned at 35 days, so offering
                6M there would quietly return 35 days labelled as six months. */}
            {(["summary", "details", "trends"].includes(section) ? periodOptionsWithLong : periodOptions)
              .map(([key, label]) => (
              <button key={key} className={period === key ? "active" : ""} onClick={() => onPeriodChange(key)} disabled={periodLoading}>{label}</button>
            ))}
          </div>
          {onRun && (
            <button className="sc-refresh-btn" onClick={onRun} disabled={running}>
              <Play size={14} />
              {running ? "Refreshing..." : "Refresh"}
            </button>
          )}
        </div>
      </div>

      <div className="scorecard-filterbar">
        <label>
          <span>Workspace</span>
          <select value={workspaceFilter || "all"} onChange={(event) => onWorkspaceChange(event.target.value)}>
            <option value="all">All workspaces</option>
            {(workspaceOptions || []).map((workspace) => (
              <option key={workspace} value={workspace}>{workspace}</option>
            ))}
          </select>
          <span className="metric-checkbox">
            <input type="checkbox" checked={excludeBaton} onChange={(event) => onExcludeBatonChange(event.target.checked)} />
            <span>Exclude Baton</span>
          </span>
        </label>
        <label>
          <span>Platform</span>
          <select value={platform || "all"} onChange={(event) => onPlatformChange(event.target.value)}>
            <option value="all">Combined (EB + PlusVibe)</option>
            <option value="emailbison">EmailBison only</option>
            <option value="plusvibe">PlusVibe only</option>
          </select>
        </label>
        <label>
          <span>Start date</span>
          <input type="date" value={draftStart} onChange={(event) => setDraftStart(event.target.value)} />
        </label>
        <label>
          <span>End date</span>
          <input type="date" value={draftEnd} onChange={(event) => setDraftEnd(event.target.value)} />
        </label>
        <label className="filter-actions">
          <span>&nbsp;</span>
          <div className="filter-actions-row">
            <button className="secondary-btn" disabled={!draftStart || !draftEnd} onClick={() => onDateFilterApply(draftStart, draftEnd)}>Apply dates</button>
            {hasCustomDates && (
              <button className="ghost-btn" onClick={onDateFilterClear}>Clear dates</button>
            )}
          </div>
        </label>
      </div>


      {section === "summary" && (
        <>
          <div className="attention-strip">
            <span className="attn-label">Needs attention</span>
            {attnItems.length === 0 ? (
              <span className="attn-chip attn-ok">All clear</span>
            ) : attnItems.map((it) => (
              <button key={it.key} className={"attn-chip attn-" + it.tone} onClick={() => setSection(it.go)}>{it.text}</button>
            ))}
          </div>
          <div className="score-metrics">
            <div className="score-card">
              <ScoreBadge status={bounceStatus} />
              <strong>{bounceCount}</strong>
              <span>Workspaces above 2% bounce</span>
              <small>{activePeriodLabel}, webhook bounces / sends</small>
              <div className="card-trend"><Sparkline values={sparkBounce} color="#e0553b" /><DeltaChip value={bounceDelta} unit="pp" goodIsUp={false} /></div>
            </div>
            <div className="score-card">
              <ScoreBadge status={replyStatus} />
              <strong>{replyRate.toFixed(2)}%</strong>
              <span>Any reply rate</span>
              <small>{activePeriodLabel}, {numberFmt(uniqueReplyCount)} unique repliers, {numberFmt(replyEventCount)} events</small>
              <div className="card-trend"><Sparkline values={sparkReply} color="#2e7d5b" /><DeltaChip value={replyDelta} unit="pp" goodIsUp={true} /></div>
            </div>
            <div className="score-card">
              <ScoreBadge status={epp ? "ok" : "watch"} />
              <strong>{epp ? epp.toLocaleString(undefined, { maximumFractionDigits: 1 }) : "N/A"}</strong>
              <span>Emails per positive</span>
              <small>{activePeriodLabel}, using webhook positives</small>
              <div className="card-trend"><Sparkline values={sparkPositives} color="#3564a4" /><DeltaChip value={positivesDelta} unit="pct" goodIsUp={true} /></div>
            </div>
          </div>
          <InfraHealthPanel data={health} period={period} />
        </>
      )}

      {section === "details" && <DetailsTab workspaces={health?.workspaces?.length ? health.workspaces : workspaces} period={period} health={health} />}

      {section === "trends" && <InfraTrendsPanel data={trends} period={period} />}
      {section === "campaign-bounces" && <CampaignBouncesTab data={campaignBounces} period={period} />}

      {false && section === "details" && (
        <>
          <section className="score-table-panel tag-panel">
            <div className="section-title compact">
              <Tags size={18} />
              <div>
                <h2>Scorecard Split</h2>
                <p>Snapshot baseline used as send/reply backup while webhook metrics build history.</p>
              </div>
            </div>
            <div className="tag-summary-grid">
              {tagSummary.map((row) => (
                <div className="tag-summary-card" key={row.infra_group}>
                  <ScoreBadge status={Number(row.reply_rate || 0) <= 2 ? "red" : Number(row.reply_rate || 0) < 3 ? "watch" : "ok"} />
                  <strong>{row.infra_label}</strong>
                  <div className="tag-stats">
                    <span><b>{Number(row.mailbox_count || 0).toLocaleString()}</b> mailboxes</span>
                    <span><b>{Number(row.sent_delta || 0).toLocaleString()}</b> sent</span>
                    <span><b>{Number(row.bounce_rate || 0).toFixed(2)}%</b> bounce</span>
                    <span><b>{Number(row.reply_rate || 0).toFixed(2)}%</b> reply</span>
                    <span><b>{Number(row.epp || 0) ? Number(row.epp || 0).toLocaleString(undefined, { maximumFractionDigits: 1 }) : "N/A"}</b> EPP</span>
                  </div>
                </div>
              ))}
              {tagSummary.length === 0 && <div className="empty-state">Tagwise data will appear after the next scorecard run.</div>}
            </div>
          </section>

          <div className="scorecard-grid">
            <section className="score-table-panel">
              <div className="section-title compact">
                <BarChart3 size={18} />
                <div>
                  <h2>Workspace Drilldown</h2>
                  <p>Sorted by bounce risk first, then reply rate</p>
                </div>
              </div>
              <div className="score-table-wrap">
                <table className="score-table">
                  <thead>
                    <tr>
                      <th>Workspace</th>
                      <th>Sent</th>
                      <th>Bounce</th>
                      <th>Reply</th>
                      <th>EPP</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workspaces.map((row) => (
                      <tr key={`${row.run_id}-${row.workspace_id}-${row.workspace_name}`}>
                        <td>
                          <strong>{row.workspace_name}</strong>
                          <span>{row.mailbox_count} mailboxes</span>
                        </td>
                        <td>{Number(row.sent_delta || 0).toLocaleString()}</td>
                        <td>{Number(row.bounce_rate || 0).toFixed(2)}%</td>
                        <td>{Number(row.reply_rate || 0).toFixed(2)}%</td>
                        <td>{Number(row.epp || 0) ? Number(row.epp || 0).toLocaleString(undefined, { maximumFractionDigits: 1 }) : "N/A"}</td>
                        <td><ScoreBadge status={row.bounce_status === "red" || row.reply_status === "red" ? "red" : row.reply_status} /></td>
                      </tr>
                    ))}
                    {workspaces.length === 0 && (
                      <tr><td colSpan={6} className="table-empty">No scorecard data yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <aside className="score-history">
              <div className="section-title compact">
                <History size={18} />
                <div>
                  <h2>Recent Runs</h2>
                  <p>Latest scorecard snapshots</p>
                </div>
              </div>
              {history.map((run) => (
                <div className="history-row" key={run.id}>
                  <div>
                    <strong>{fmtDate(run.started_at)}</strong>
                    <span>{run.basis === "rolling_7d" ? "Rolling 7 days" : "Snapshot baseline"}</span>
                  </div>
                  <div>
                    <b>{run.bounce_risk_workspace_count}</b>
                    <span>bounce risks</span>
                  </div>
                  <div>
                    <b>{Number(run.reply_rate || 0).toFixed(2)}%</b>
                    <span>reply</span>
                  </div>
                </div>
              ))}
              {history.length === 0 && <div className="empty-state">No scorecard runs yet.</div>}
            </aside>
          </div>

          <section className="score-table-panel tag-detail-panel">
            <div className="section-title compact">
              <BarChart3 size={18} />
              <div>
                <h2>Tagwise Workspace Detail</h2>
                <p>Mailbox performance split by infra group and workspace</p>
              </div>
            </div>
            <div className="score-table-wrap">
              <table className="score-table">
                <thead>
                  <tr>
                    <th>Infra</th>
                    <th>Workspace</th>
                    <th>Mailboxes</th>
                    <th>Sent</th>
                    <th>Bounce</th>
                    <th>Reply</th>
                    <th>EPP</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {tagDetails.map((row) => (
                    <tr key={`${row.run_id}-${row.workspace_id}-${row.infra_group}`}>
                      <td><strong>{row.infra_label}</strong></td>
                      <td>{row.workspace_name}</td>
                      <td>{Number(row.mailbox_count || 0).toLocaleString()}</td>
                      <td>{Number(row.sent_delta || 0).toLocaleString()}</td>
                      <td>{Number(row.bounce_rate || 0).toFixed(2)}%</td>
                      <td>{Number(row.reply_rate || 0).toFixed(2)}%</td>
                      <td>{Number(row.epp || 0) ? Number(row.epp || 0).toLocaleString(undefined, { maximumFractionDigits: 1 }) : "N/A"}</td>
                      <td><ScoreBadge status={row.bounce_status === "red" || row.reply_status === "red" ? "red" : row.reply_status} /></td>
                    </tr>
                  ))}
                  {tagDetails.length === 0 && <tr><td colSpan={8} className="table-empty">No tagwise details yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {section === "domains" && <DomainIntelligencePanel data={domains} onExecuteDomain={onExecuteDomain} executingDomain={executingDomain} />}

      {section === "events" && (
        <>
          <PositiveLeadsPanel data={positives} period={period} />
          <BounceEventsPanel data={bounces} period={period} />
        </>
      )}

      {section === "cpp" && <CppTab token={token} isAdmin={isAdmin} />}
      {section === "definitions" && <DefinitionsTab token={token} isAdmin={isAdmin} />}
    </section>
  );
}

function DomainRemovalPage({ data, busy, onRefresh, onCancel, onConfirmRemove, onRemoveAll, candidates, candidatesLoading, onScanCandidates, onHold, onHoldAll }) {
  const statusOrder = { held: 0, removed: 1, cancelled: 2 };
  const rows = [...(data?.queue || [])].sort((a, b) =>
    (statusOrder[a.status] ?? 3) - (statusOrder[b.status] ?? 3) ||
    (b.metrics?.complaint_rate ?? 0) - (a.metrics?.complaint_rate ?? 0) ||
    (b.metrics?.bounce_rate ?? 0) - (a.metrics?.bounce_rate ?? 0)
  );
  const queued = new Set(rows.filter((r) => r.status === "held" || r.status === "removed").map((r) => r.domain));
  const candList = (candidates?.candidates || []).filter((c) => !queued.has(c.domain));
  const candSummary = candidates?.summary || {};
  const removeCands = candList.filter((c) => c.verdict === "remove");
  const holdAllActive = String(busy || "").startsWith("__all__:");
  const holdAllParts = holdAllActive ? String(busy).split(":") : [];
  const holdAllLabel = holdAllActive ? `Holding ${holdAllParts[1]} (${holdAllParts[2]})…` : `Hold all (${removeCands.length})`;
  const held = rows.filter((r) => r.status === "held");
  const ready = held.filter((r) => r.ready_to_remove);
  const removed = rows.filter((r) => r.status === "removed");
  const cancelled = rows.filter((r) => r.status === "cancelled");
  return (
    <section className="removal-page">
      <div className="removal-head">
        <h2>Domain Removal</h2>
        <button className="btn-small" onClick={onRefresh}>Refresh</button>
      </div>
      <div className="removal-stats">
        <span>Held: <b>{held.length}</b></span>
        <span>Ready to remove: <b>{ready.length}</b></span>
        <span>Removed: <b>{removed.length}</b></span>
        <span>Cancelled: <b>{cancelled.length}</b></span>
      </div>
      <p className="removal-note">Held domains have sending stopped (daily limit 1, warmup off) and are skipped by rotation. "Remove" (permanent mailbox deletion) unlocks only after the 7-day grace date.</p>
      <div className="removal-head">
        <h3>Find new candidates</h3>
        <button className="btn-small" disabled={candidatesLoading} onClick={onScanCandidates}>
          {candidatesLoading ? "Scanning… (takes ~1 min)" : "Scan candidates (30d)"}
        </button>
      </div>
      {candidates && (
        <>
          <div className="removal-stats">
            <span>Remove: <b>{candSummary.remove ?? 0}</b></span>
            <span>Review: <b>{candSummary.review ?? 0}</b></span>
            <span>Keep: <b>{candSummary.keep ?? 0}</b></span>
            <span>Insufficient data: <b>{candSummary.insufficient ?? 0}</b></span>
            <span>Protected (skipped): <b>{candSummary.protected_skipped ?? 0}</b></span>
            {removeCands.length > 0 && (
              <button className="btn-small btn-danger" disabled={!!busy} onClick={() => onHoldAll(removeCands.map((c) => c.domain))}>
                {holdAllLabel}
              </button>
            )}
          </div>
          <div className="rm-card">
          <table className="removal-table">
            <thead>
              <tr><th>Domain</th><th>Verdict</th><th>Complaint %</th><th>Block %</th><th>Bounce %</th><th>Sent</th><th>Positives</th><th>Workspaces</th><th>Reasons</th><th></th></tr>
            </thead>
            <tbody>
              {candList.map((c) => (
                <tr key={c.domain}>
                  <td>{c.domain}</td>
                  <td><span className={`rm-status rm-${c.verdict === "remove" ? "ready" : "held"}`}>{c.verdict}</span></td>
                  <td>{c.complaint_rate ?? "—"}</td>
                  <td>{c.block_rate ?? "—"}</td>
                  <td>{c.bounce_rate ?? "—"}</td>
                  <td>{c.sent}</td>
                  <td>{c.positives ?? 0}</td>
                  <td className="rm-reason" title={(c.workspaces || []).join(", ")}>{(c.workspaces || []).join(", ")}</td>
                  <td className="rm-reason" title={(c.reasons || []).join("; ")}>{(c.reasons || []).join("; ")}</td>
                  <td className="rm-actions">
                    {c.verdict === "remove" && (
                      <button className="btn-small btn-danger" disabled={!!busy} onClick={() => onHold(c.domain)}>
                        {busy === c.domain ? "Holding…" : "Hold"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {candList.length === 0 && <tr><td colSpan={10} className="rm-empty">No new candidates — everything flagged is already in the queue below.</td></tr>}
            </tbody>
          </table>
          </div>
        </>
      )}
      <div className="removal-head">
        <h3>Removal queue</h3>
        {ready.length > 0 && (
          <button
            className="btn-small btn-danger"
            disabled={!!busy}
            onClick={() => onRemoveAll(ready.map((r) => r.domain))}
          >
            {String(busy || "").startsWith("__all__:")
              ? `Removing ${String(busy).split(":")[1]} (${String(busy).split(":")[2]})…`
              : `Remove all ready (${ready.length})`}
          </button>
        )}
      </div>
      <div className="rm-card">
      <table className="removal-table">
        <thead>
          <tr><th>Domain</th><th>Status</th><th>Complaint %</th><th>Bounce %</th><th>Mailboxes</th><th>Held</th><th>Remove on</th><th>Reason</th><th></th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.domain}>
              <td>{r.domain}</td>
              <td><span className={`rm-status rm-${r.ready_to_remove ? "ready" : r.status}`}>{r.ready_to_remove ? "ready" : r.status}</span></td>
              <td>{r.metrics?.complaint_rate ?? "—"}%</td>
              <td>{r.metrics?.bounce_rate ?? "—"}%</td>
              <td>{r.mailboxes_held}</td>
              <td>{r.held_at ? String(r.held_at).slice(0, 10) : "—"}</td>
              <td>{r.scheduled_removal_at ? String(r.scheduled_removal_at).slice(0, 10) : "—"}</td>
              <td className="rm-reason" title={r.reason}>{r.reason}</td>
              <td className="rm-actions">
                {r.status === "held" && (
                  <>
                    <button className="btn-small" disabled={!!busy} onClick={() => onCancel(r.domain)}>Cancel</button>
                    <button
                      className="btn-small btn-danger"
                      disabled={!!busy || !r.ready_to_remove}
                      title={r.ready_to_remove ? "Permanently delete all mailboxes" : `Locked until ${String(r.scheduled_removal_at).slice(0, 10)}`}
                      onClick={() => onConfirmRemove(r.domain)}
                    >Remove</button>
                  </>
                )}
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={9} className="rm-empty">No domains in the removal pipeline.</td></tr>}
        </tbody>
      </table>
      </div>
    </section>
  );
}

function DomainAgePage({ data, trend, period, loading, onPeriod }) {
  const cohorts = data?.cohorts || [];
  const cols = data?.columns || ["AO", "Outlook", "All Own Infra"];
  const fmtRR = (v) => (v == null ? "—" : `${v.toFixed(2)}%`);
  const num = (n) => (n || 0).toLocaleString();
  const cohortTags = (trend?.cohort_order || []).map((c) => ({ infra_tag: c }));
  return (
    <section className="removal-page">
      <div className="removal-head">
        <h2>Domain Age vs Reply Rate</h2>
        <div className="period-seg">
          {["7d", "14d", "30d"].map((p) => (
            <button key={p} className={period === p ? "btn-small btn-active" : "btn-small"} onClick={() => onPeriod(p)}>{p}</button>
          ))}
        </div>
      </div>
      <p className="removal-note">
        Own-infra sending domains bucketed by age (purchase date from the MASTER sheet). RR = unique leads who replied ÷ unique leads contacted in the window; Human RR excludes auto-replies.
        {data ? ` ${num(data.purchase_dates_loaded)} domains dated, ${data.unmatched_domains} unmatched.` : ""}
      </p>
      {loading && <p className="removal-note">Loading…</p>}
      {!loading && cols.map((col) => (
        <div key={col}>
          <div className="removal-head"><h3>{col}</h3></div>
          <div className="rm-card">
            <table className="removal-table">
              <thead>
                <tr><th>Age cohort</th><th>Domains</th><th>Sent</th><th>Contacted</th><th>Repliers</th><th>RR %</th><th>Human RR %</th></tr>
              </thead>
              <tbody>
                {cohorts.map((c) => {
                  const x = c.by_tag[col] || {};
                  return (
                    <tr key={c.label}>
                      <td>{c.label}</td>
                      <td>{num(x.domains)}</td>
                      <td>{num(x.sent)}</td>
                      <td>{num(x.contacted)}</td>
                      <td>{num(x.repliers)}</td>
                      <td><b>{fmtRR(x.rr)}</b></td>
                      <td>{fmtRR(x.human_rr)}</td>
                    </tr>
                  );
                })}
                {cohorts.length === 0 && <tr><td colSpan={7} className="rm-empty">No own-infra activity in this window.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {trend && (trend.months || []).length > 0 && (
        <div className="domain-age-trend">
          <div className="removal-head"><h2>Aging curve — monthly RR by age-at-send</h2></div>
          <p className="removal-note">
            Each month, every domain is bucketed by how old it was <i>that month</i> (not today), so this shows whether domains genuinely improve as they age — controlling for survivorship. {(trend.months || []).length} month(s) of history so far; the curve deepens as more accrues.
          </p>
          {(trend.columns || []).map((col) => (
            <TrendLineChart
              key={col}
              title={`${col} — aging curve`}
              subtitle="Monthly reply rate (unique repliers ÷ contacted), one line per age cohort"
              data={trend.series?.[col]}
              tags={cohortTags}
              metric="rr"
              formatValue={(v) => `${Number(v || 0).toFixed(2)}%`}
              emptyLabel="No own-infra activity yet."
            />
          ))}
        </div>
      )}
    </section>
  );
}

export default function App() {
  const [token, setToken] = useState(() => localStorage.getItem("rgl_token"));
  const [username, setUsername] = useState(() => localStorage.getItem("rgl_user") || "");
  const [role, setRole] = useState(() => localStorage.getItem("rgl_role") || "");
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [authError, setAuthError] = useState("");
  const isAdmin = role === "admin";
  const [stats, setStats] = useState({});
  const [automationState, setAutomationState] = useState({ items: [] });
  const [logs, setLogs] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [notice, setNotice] = useState("");
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [busyKey, setBusyKey] = useState("");
  const [stoppingRunId, setStoppingRunId] = useState("");
  const [stoppingLogId, setStoppingLogId] = useState(null);
  const [activeView, setActiveView] = useState("l10");
  const [theme, setTheme] = useState(() => { try { return localStorage.getItem("rgl-theme") || "dark"; } catch (e) { return "dark"; } });
  useEffect(() => { document.documentElement.setAttribute("data-theme", theme); try { localStorage.setItem("rgl-theme", theme); } catch (e) {} }, [theme]);
  const [infraSection, setInfraSection] = useState("summary");
  const [infraNavOpen, setInfraNavOpen] = useState(true);
  const [l10Scorecard, setL10Scorecard] = useState({ latest: null, workspaces: [], history: [] });
  const [positiveLeads, setPositiveLeads] = useState({ total: 0, infra: [], workspaces: [] });
  const [bounceEvents, setBounceEvents] = useState({ total: 0, categories: [], infra: [], recipient_domains: [] });
  const [infraHealth, setInfraHealth] = useState({ totals: {}, infra: [] });
  const [domainIntelligence, setDomainIntelligence] = useState({ summary: {}, sender_domains: [], recipient_domains: [] });
  const [removalQueue, setRemovalQueue] = useState({ queue: [], count: 0 });
  const [removalBusy, setRemovalBusy] = useState("");
  const [removalCandidates, setRemovalCandidates] = useState(null);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [domainAgeRR, setDomainAgeRR] = useState(null);
  const [domainAgePeriod, setDomainAgePeriod] = useState("30d");
  const [domainAgeLoading, setDomainAgeLoading] = useState(false);
  const [domainAgeTrend, setDomainAgeTrend] = useState(null);
  const [infraTrends, setInfraTrends] = useState({ totals: {}, tags: [], buckets: [] });
  const [campaignBounces, setCampaignBounces] = useState({ campaigns: [], min_sent: 100 });
  const [dataTables, setDataTables] = useState({ tables: [], rows: [], columns: [] });
  const [selectedDataTable, setSelectedDataTable] = useState("sent_events");
  const [l10Running, setL10Running] = useState(false);
  const [calculationPeriod, setCalculationPeriod] = useState("7d");
  const [workspaceFilter, setWorkspaceFilter] = useState("all");
  const [excludeBaton, setExcludeBaton] = useState(false);
  const [platform, setPlatform] = useState("all");  // all | emailbison | plusvibe
  const [dateFilter, setDateFilter] = useState({ startDate: "", endDate: "" });
  const [periodLoading, setPeriodLoading] = useState(false);
  const [executingDomain, setExecutingDomain] = useState("");
  const metricsRequestRef = useRef(0);
  const eventsRequestRef = useRef(0);
  const domainsRequestRef = useRef(0);
  const trendsRequestRef = useRef(0);
  const campaignBouncesRequestRef = useRef(0);

  const headers = useMemo(() => authHeader(token), [token]);
  const metricFilterKey = `${workspaceFilter}|${excludeBaton}|${platform}|${dateFilter.startDate}|${dateFilter.endDate}`;
  const effectiveMetricPeriod = dateFilter.startDate && dateFilter.endDate ? "custom" : calculationPeriod;
  const workspaceOptions = useMemo(() => {
    const names = new Set();
    [...(l10Scorecard?.workspaces || []), ...(infraHealth?.workspaces || [])].forEach((row) => {
      const name = row.workspace_name || row.name;
      if (name && name !== "Unknown" && name !== "Codex Smoke Test") names.add(name);
    });
    // Also list every client that has PlusVibe mailboxes (incl. onboarding clients
    // with no sends yet), so the client list is complete, not activity-gated.
    (infraHealth?.pv_inventory?.by_client || []).forEach((c) => {
      const name = c.client;
      if (name && name !== "Unknown" && name !== "(unnamed)" && name !== "EmailBison GR (retained)") names.add(name);
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [infraHealth?.workspaces, l10Scorecard?.workspaces, infraHealth?.pv_inventory]);

  const metricQuery = useCallback((period, overrides = {}) => {
    const query = new URLSearchParams();
    const nextWorkspace = overrides.workspaceFilter ?? workspaceFilter;
    const nextExcludeBaton = overrides.excludeBaton ?? excludeBaton;
    const nextPlatform = overrides.platform ?? platform;
    const nextDate = overrides.dateFilter ?? dateFilter;
    const nextPeriod = nextDate.startDate && nextDate.endDate ? "custom" : period;
    query.set("period", nextPeriod);
    if (nextWorkspace && nextWorkspace !== "all") query.set("workspace", nextWorkspace);
    if (nextExcludeBaton) query.set("exclude_baton", "true");
    if (nextPlatform && nextPlatform !== "all") query.set("platform", nextPlatform);
    if (nextDate.startDate && nextDate.endDate) {
      query.set("start_date", nextDate.startDate);
      query.set("end_date", nextDate.endDate);
    }
    return query.toString();
  }, [dateFilter, excludeBaton, platform, workspaceFilter]);

  // Read-only auto-login: mint a public viewer token so the embed never prompts.
  const loadViewer = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/auth/viewer-token`);
      localStorage.setItem("rgl_token", r.data.access_token);
      localStorage.setItem("rgl_user", r.data.username);
      localStorage.setItem("rgl_role", r.data.role);
      setToken(r.data.access_token);
      setUsername(r.data.username);
      setRole(r.data.role);
      setAuthError("");
    } catch (e) {
      setAuthError(e.response?.data?.detail || e.message || "Could not load dashboard");
    }
  }, []);

  // logout downgrades back to the read-only viewer rather than to a login wall.
  const logout = useCallback(() => {
    localStorage.removeItem("rgl_token");
    localStorage.removeItem("rgl_user");
    localStorage.removeItem("rgl_role");
    setToken(null);
    setRole("");
    setUsername("");
    loadViewer();
  }, [loadViewer]);

  useEffect(() => {
    if (!token) loadViewer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isAdmin && ["automations", "removal", "logs", "data"].includes(activeView)) setActiveView("l10");
  }, [isAdmin, activeView]);

  const fetchMetrics = useCallback(async (period, showLoading = false, overrides = {}) => {
    if (!token) return;
    const requestId = ++metricsRequestRef.current;
    const query = metricQuery(period, overrides);
    if (showLoading) setPeriodLoading(true);
    try {
      const [scorecard, health] = await Promise.all([
        axios.get(`${API}/l10-scorecard`, headers),
        axios.get(`${API}/infra-health?${query}`, headers),
      ]);
      if (requestId !== metricsRequestRef.current) return;
      setL10Scorecard(scorecard.data);
      setInfraHealth(health.data);
    } catch (e) {
      if (e.response?.status === 401) logout();
      else setNotice(e.response?.data?.detail || e.message);
    } finally {
      if (requestId === metricsRequestRef.current) setPeriodLoading(false);
    }
  }, [headers, logout, metricQuery, token]);

  const fetchEvents = useCallback(async (period, overrides = {}) => {
    if (!token) return;
    const requestId = ++eventsRequestRef.current;
    const query = metricQuery(rawPeriod(period), overrides);
    try {
      const [positives, bounces] = await Promise.all([
        axios.get(`${API}/positive-leads?${query}`, headers),
        axios.get(`${API}/bounce-events?${query}`, headers),
      ]);
      if (requestId !== eventsRequestRef.current) return;
      setPositiveLeads(positives.data);
      setBounceEvents(bounces.data);
    } catch (e) {
      if (e.response?.status === 401) logout();
      else setNotice(e.response?.data?.detail || e.message);
    }
  }, [headers, logout, metricQuery, token]);

  const fetchDomains = useCallback(async (period, overrides = {}) => {
    period = rawPeriod(period);
    if (!token) return;
    const requestId = ++domainsRequestRef.current;
    const query = metricQuery(period, overrides);
    try {
      const domains = await axios.get(`${API}/domain-intelligence?${query}`, headers);
      if (requestId !== domainsRequestRef.current) return;
      setDomainIntelligence(domains.data);
    } catch (e) {
      if (e.response?.status === 401) logout();
      else setNotice(e.response?.data?.detail || e.message);
    }
  }, [headers, logout, metricQuery, token]);

  const fetchTrends = useCallback(async (period, overrides = {}) => {
    if (!token) return;
    const requestId = ++trendsRequestRef.current;
    const query = metricQuery(period, overrides);
    setInfraTrends((current) => ({ ...current, loading: true }));
    try {
      const trends = await axios.get(`${API}/infra-trends?${query}`, headers);
      if (requestId !== trendsRequestRef.current) return;
      setInfraTrends({ ...trends.data, loading: false });
    } catch (e) {
      if (requestId === trendsRequestRef.current) setInfraTrends((current) => ({ ...current, loading: false }));
      if (e.response?.status === 401) logout();
      else setNotice(e.response?.data?.detail || e.message);
    }
  }, [headers, logout, metricQuery, token]);

  const fetchCampaignBounces = useCallback(async (period, overrides = {}) => {
    period = rawPeriod(period);
    if (!token) return;
    const requestId = ++campaignBouncesRequestRef.current;
    const query = metricQuery(period, overrides);
    try {
      const r = await axios.get(`${API}/campaign-bounces?${query}`, headers);
      if (requestId !== campaignBouncesRequestRef.current) return;
      setCampaignBounces(r.data);
    } catch (e) {
      if (e.response?.status === 401) logout();
      else setNotice(e.response?.data?.detail || e.message);
    }
  }, [headers, logout, metricQuery, token]);

  // Refresh every dataset (all tabs) in parallel for one period/filter change.
  const fetchAll = useCallback((period, overrides = {}) => {
    fetchMetrics(period, true, overrides);
    fetchEvents(period, overrides);
    fetchDomains(period, overrides);
    fetchTrends(period, overrides);
  }, [fetchMetrics, fetchEvents, fetchDomains, fetchTrends]);

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const reqs = [
        axios.get(`${API}/stats`, headers),
        axios.get(`${API}/automations`, headers),
      ];
      if (isAdmin) reqs.push(axios.get(`${API}/logs?limit=40`, headers));
      const [s, a, l] = await Promise.all(reqs);
      setStats(s.data);
      setAutomationState(a.data);
      if (isAdmin && l) setLogs(l.data.filter((log) => (log.job_name || "").startsWith("EmailBison -")));
      setLastRefresh(new Date());
      if (!periodLoading) fetchMetrics(effectiveMetricPeriod, false);
      if (isAdmin) axios.get(`${API}/data-tables?table=${selectedDataTable}&limit=50`, headers).then((r) => setDataTables(r.data)).catch(() => {});
    } catch (e) {
      if (e.response?.status === 401) logout();
      else setNotice(e.response?.data?.detail || e.message);
    }
  }, [effectiveMetricPeriod, fetchMetrics, headers, isAdmin, logout, periodLoading, selectedDataTable, token]);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, 15000);
    return () => clearInterval(timer);
  }, [refresh]);

  const changeCalculationPeriod = useCallback((period) => {
    setCalculationPeriod(period);
    const clearedDateFilter = { startDate: "", endDate: "" };
    setDateFilter(clearedDateFilter);
    fetchAll(period, { dateFilter: clearedDateFilter });
  }, [fetchAll]);

  const changeWorkspaceFilter = useCallback((workspace) => {
    setWorkspaceFilter(workspace);
    fetchAll(effectiveMetricPeriod, { workspaceFilter: workspace });
  }, [effectiveMetricPeriod, fetchAll]);

  const changeExcludeBaton = useCallback((checked) => {
    setExcludeBaton(checked);
    fetchAll(effectiveMetricPeriod, { excludeBaton: checked });
  }, [effectiveMetricPeriod, fetchAll]);

  const changePlatform = useCallback((value) => {
    setPlatform(value);
    fetchAll(effectiveMetricPeriod, { platform: value });
  }, [effectiveMetricPeriod, fetchAll]);

  const applyDateFilter = useCallback((startDate, endDate) => {
    const nextDateFilter = { startDate, endDate };
    setDateFilter(nextDateFilter);
    fetchAll("custom", { dateFilter: nextDateFilter });
  }, [fetchAll]);

  const clearDateFilter = useCallback(() => {
    const nextDateFilter = { startDate: "", endDate: "" };
    setDateFilter(nextDateFilter);
    fetchAll(calculationPeriod, { dateFilter: nextDateFilter });
  }, [calculationPeriod, fetchAll]);

  const runAutomation = async (key, payload) => {
    setBusyKey(key);
    setNotice("");
    try {
      const r = await axios.post(`${API}/automations/${key}/run`, payload, headers);
      setNotice(`Started run ${r.data.run_id.slice(0, 8)}. Logs will update automatically.`);
      setTimeout(refresh, 800);
    } catch (e) {
      setNotice(e.response?.data?.detail || e.message);
    } finally {
      setBusyKey("");
    }
  };

  const stopAutomationRun = async (runId) => {
    if (!runId) return;
    setStoppingRunId(runId);
    setNotice("");
    try {
      await axios.post(`${API}/automations/runs/${runId}/stop`, {}, headers);
      setNotice("Stop requested. The run will update to cancelled after the process exits.");
      setTimeout(refresh, 800);
    } catch (e) {
      setNotice(e.response?.data?.detail || e.message);
    } finally {
      setStoppingRunId("");
    }
  };

  const stopAutomationLog = async (log) => {
    if (!log?.id) return;
    setStoppingLogId(log.id);
    setNotice("");
    try {
      await axios.post(`${API}/automations/runs/by-log/${log.id}/stop`, {}, headers);
      setNotice("Stop requested. The run will update to cancelled after the process exits.");
      setTimeout(refresh, 800);
    } catch (e) {
      setNotice(e.response?.data?.detail || e.message);
    } finally {
      setStoppingLogId(null);
    }
  };

  const fetchRemovalQueue = async () => {
    try {
      const r = await axios.get(`${API}/domain-removal/queue`, authHeader(token));
      setRemovalQueue(r.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (activeView === "removal") fetchRemovalQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView]);

  const fetchDomainAgeRR = async (period) => {
    setDomainAgeLoading(true);
    try {
      const r = await axios.get(`${API}/domain-age-rr?period=${period || domainAgePeriod}`, authHeader(token));
      setDomainAgeRR(r.data);
    } catch (e) { /* ignore */ } finally { setDomainAgeLoading(false); }
  };

  const fetchDomainAgeTrend = async () => {
    try {
      const r = await axios.get(`${API}/domain-age-rr-trend?months=6`, authHeader(token));
      setDomainAgeTrend(r.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (activeView === "domainage") { fetchDomainAgeRR(domainAgePeriod); if (!domainAgeTrend) fetchDomainAgeTrend(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, domainAgePeriod]);

  const cancelDomainRemoval = async (domain) => {
    if (!window.confirm(`Cancel removal of ${domain}? This un-holds it (next rotation restores limits).`)) return;
    setRemovalBusy(domain);
    try {
      await axios.post(`${API}/domain-removal/${encodeURIComponent(domain)}/cancel`, {}, authHeader(token));
      await fetchRemovalQueue();
    } catch (e) {
      window.alert(`Could not cancel ${domain}: ${e?.response?.data?.detail || e.message}`);
    } finally { setRemovalBusy(""); }
  };

  const removeOneDomain = async (domain) => {
    const r = await axios.post(`${API}/domain-removal/${encodeURIComponent(domain)}/remove`, { confirm: true }, authHeader(token));
    return r.data;
  };

  const confirmDomainRemove = async (domain) => {
    if (!window.confirm(`PERMANENTLY DELETE all mailboxes for ${domain}? This cannot be undone.`)) return;
    setRemovalBusy(domain);
    try {
      const r = await removeOneDomain(domain);
      window.alert(`${domain}: deleted ${r.deleted}/${r.mailbox_count} mailboxes (failed ${r.failed}).`);
      await fetchRemovalQueue();
    } catch (e) {
      window.alert(`Could not remove ${domain}: ${e?.response?.data?.detail || e.message}`);
    } finally { setRemovalBusy(""); }
  };

  const removeAllReadyDomains = async (domains) => {
    if (!domains.length) return;
    const typed = window.prompt(`PERMANENTLY DELETE all mailboxes for ALL ${domains.length} ready domains? This cannot be undone.\n\nType DELETE to confirm:`);
    if (typed !== "DELETE") return;
    let okDomains = 0, okMailboxes = 0, failed = [];
    for (let i = 0; i < domains.length; i++) {
      const d = domains[i];
      setRemovalBusy(`__all__:${d}:${i + 1}/${domains.length}`);
      try {
        const r = await removeOneDomain(d);
        okDomains += 1;
        okMailboxes += (r.deleted || 0);
      } catch (e) {
        failed.push(d);
      }
    }
    setRemovalBusy("");
    await fetchRemovalQueue();
    window.alert(`Remove all finished: ${okDomains}/${domains.length} domains deleted (${okMailboxes} mailboxes).${failed.length ? ` Failed: ${failed.join(", ")}` : ""}`);
  };

  const scanRemovalCandidates = async () => {
    setCandidatesLoading(true);
    try {
      const r = await axios.get(`${API}/domain-removal/candidates?period=30d`, authHeader(token));
      setRemovalCandidates(r.data);
    } catch (e) {
      window.alert(`Candidate scan failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setCandidatesLoading(false); }
  };

  const holdOneDomain = async (domain) => {
    const r = await axios.post(`${API}/domain-removal/hold`, { domains: [domain], confirm: true, period: "30d" }, authHeader(token));
    const res = (r.data.results || [])[0] || {};
    setRemovalCandidates((prev) => prev ? { ...prev, candidates: (prev.candidates || []).filter((c) => c.domain !== domain) } : prev);
    return res;
  };

  const holdRemovalDomain = async (domain) => {
    if (!window.confirm(`Hold ${domain}? Sending stops (daily limit 1, warmup off) and it is queued for removal after the 7-day grace.`)) return;
    setRemovalBusy(domain);
    try {
      const res = await holdOneDomain(domain);
      window.alert(`${domain}: held ${res.held || 0}/${res.mailbox_count || 0} mailboxes (failed ${res.failed || 0}).`);
      await fetchRemovalQueue();
    } catch (e) {
      window.alert(`Could not hold ${domain}: ${e?.response?.data?.detail || e.message}`);
    } finally { setRemovalBusy(""); }
  };

  const holdAllRemovalDomains = async (domains) => {
    if (!domains.length) return;
    if (!window.confirm(`Hold ALL ${domains.length} REMOVE-verdict domains? Sending stops on every one and each is queued for removal after the 7-day grace. This runs one domain at a time and may take several minutes.`)) return;
    let heldDomains = 0, heldMailboxes = 0, failedDomains = [];
    for (let i = 0; i < domains.length; i++) {
      const d = domains[i];
      setRemovalBusy(`__all__:${d}:${i + 1}/${domains.length}`);
      try {
        const res = await holdOneDomain(d);
        heldDomains += 1;
        heldMailboxes += res.held || 0;
      } catch (e) {
        failedDomains.push(d);
      }
    }
    setRemovalBusy("");
    await fetchRemovalQueue();
    window.alert(`Hold all finished: ${heldDomains}/${domains.length} domains held (${heldMailboxes} mailboxes).${failedDomains.length ? ` Failed: ${failedDomains.join(", ")} — re-scan and retry those.` : ""}`);
  };

  const executeDomainLimitAction = async (domain) => {
    if (!domain) return;
    const confirmed = window.confirm(`Set all known mailboxes for ${domain} to sending limit 1 and warmup limit 20 for 15 days?`);
    if (!confirmed) return;
    setExecutingDomain(domain);
    setNotice("");
    try {
      const response = await axios.post(
        `${API}/domain-intelligence/${encodeURIComponent(domain)}/execute`,
        { confirm: true, sending_limit: 1, warmup_daily_limit: 20, warmup_days: 15 },
        headers,
      );
      const result = response.data;
      const failed = Number(result.failed_count || 0);
      setNotice(`${domain}: ${numberFmt(result.updated_count)} mailbox${Number(result.updated_count) === 1 ? "" : "es"} updated${failed ? `, ${numberFmt(failed)} failed` : ""}.`);
      fetchDomains(effectiveMetricPeriod);
    } catch (e) {
      if (e.response?.status === 401) logout();
      else setNotice(e.response?.data?.detail || e.message);
    } finally {
      setExecutingDomain("");
    }
  };

  if (showAdminLogin) {
    return (
      <LoginPage
        onLogin={(newToken, user, newRole) => { setToken(newToken); setUsername(user); setRole(newRole); setShowAdminLogin(false); }}
        onBack={token ? () => setShowAdminLogin(false) : undefined}
      />
    );
  }
  if (!token) {
    return (
      <main className="login-screen">
        <section className="login-panel">
          <div className="login-mark"><ShieldCheck size={28} /></div>
          <h1>Revenue.inc Automation Console</h1>
          <p>{authError || "Loading dashboard…"}</p>
          {authError && (
            <button className="primary-btn full" onClick={loadViewer}><RefreshCw size={16} /> Retry</button>
          )}
        </section>
      </main>
    );
  }

  const runningCount = automationState.items.filter((item) => item.status === "running").length;
  const failedCount = automationState.items.filter((item) => item.status === "failed").length;
  const scheduledItems = automationState.items.filter((item) => item.next_run);
  const onDemandCount = automationState.items.length - scheduledItems.length;
  const nextRunItem = [...scheduledItems].sort((a, b) => new Date(a.next_run) - new Date(b.next_run))[0];
  const lastRunItem = automationState.items.filter((item) => item.last_run)
    .sort((a, b) => new Date(b.last_run) - new Date(a.last_run))[0];
  const capacityAutomation = automationState.items.find((item) => item.key === "eb-capacity-tracker");
  const campaignAutomation = automationState.items.find((item) => item.key === "campaign-progress-snapshot");
  const lookupAutomation = automationState.items.find((item) => item.key === "find-domain");
  const mailboxAutomations = automationState.items.filter((item) => item.mode === "mailbox_move" || item.mode === "pv_mailbox_move");
  const reconnectAutomations = automationState.items.filter((item) => item.mode === "microsoft_reconnect");
  const utilityAutomations = automationState.items.filter((item) => !["l10-scorecard-monitor", "eb-capacity-tracker", "campaign-progress-snapshot", "find-domain"].includes(item.key) && item.mode !== "mailbox_move" && item.mode !== "pv_mailbox_move" && item.mode !== "microsoft_reconnect");
  const monitoringAutomations = [capacityAutomation, campaignAutomation].filter(Boolean);
  const lookupAutomations = [lookupAutomation, ...utilityAutomations].filter(Boolean);

  const runL10Scorecard = async () => {
    setL10Running(true);
    setNotice("");
    try {
      await axios.post(`${API}/l10-scorecard/run`, {}, headers);
      setNotice("L10 scorecard refresh started. Results will update automatically.");
      setTimeout(refresh, 1800);
    } catch (e) {
      setNotice(e.response?.data?.detail || e.message);
    } finally {
      setL10Running(false);
    }
  };

  return (
    <main className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark"><Activity size={20} /></div>
          <div>
            <strong>Revenue.inc</strong>
            <span>Automation Console</span>
          </div>
        </div>
        <div className="sidebar-ws">
          <select value={workspaceFilter || "all"} onChange={(e) => changeWorkspaceFilter(e.target.value)} aria-label="Workspace">
            <option value="all">All workspaces</option>
            {(workspaceOptions || []).map((w) => (<option key={w} value={w}>{w}</option>))}
          </select>
        </div>
        <nav className="main-nav">
          {[
            ["l10", "Infra Health"],
            ["domainage", "Domain Age Trend"],
            ["removal", "Domain Removal"],
            ["automations", "Automations"],
            ["logs", "Automation Logs"],
            ["data", "Data Tables"],
          ].filter(([key]) => isAdmin || !["automations", "removal", "logs", "data"].includes(key)).map(([key, label]) => (
            <React.Fragment key={key}>
              <button className={activeView === key ? "nav-tab active" : "nav-tab"} onClick={() => { setActiveView(key); if (key === "l10") setInfraNavOpen((o) => (activeView === "l10" ? !o : true)); }}>
                {label}
                {key === "l10" && <span className="nav-caret">{infraNavOpen ? "\u25be" : "\u25b8"}</span>}
              </button>
              {key === "l10" && infraNavOpen && (
                <div className="nav-sub">
                  {[["summary", "Summary"], ["campaign-bounces", "Workspace Bounces"], ["details", "Infra Details"], ["trends", "Trends"], ["events", "Positive Leads"], ["cpp", "CPP"], ["definitions", "Targets & Definitions"]].map(([sk, sl]) => (
                    <button key={sk} className={infraSection === sk ? "nav-subtab active" : "nav-subtab"} onClick={() => { setActiveView("l10"); setInfraSection(sk); }}>{sl}</button>
                  ))}
                </div>
              )}
            </React.Fragment>
          ))}
        </nav>
        <div className="top-actions">
          <span className="refresh-note">Last refresh {format(lastRefresh, "HH:mm:ss")}</span>
          <button className="ghost-btn" onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}>{theme === "dark" ? <Sun size={15} /> : <Moon size={15} />} {theme === "dark" ? "Light mode" : "Dark mode"}</button>
          <span className="user-chip">{isAdmin ? username : "viewer · read-only"}</span>
          {isAdmin
            ? <button className="ghost-btn danger-text" onClick={logout}><LogOut size={15} /> Logout</button>
            : <button className="ghost-btn" onClick={() => setShowAdminLogin(true)}><Lock size={15} /> Admin sign in</button>}
        </div>
      </aside>

      {activeView === "automations" && <section className="hero-band">
        <div>
          <p className="eyebrow">Revenue.inc · Infrastructure operations</p>
          <h1>Revenue.inc Automations</h1>
          <p className="hero-copy">Controlled automation runs, capacity tracking, and execution visibility for the Revenue.inc infra team.</p>
        </div>
        <div className="metrics">
          <Metric icon={TerminalSquare} label="Automations" value={automationState.items.length || 0} sub="available tools" />
          <Metric icon={Clock3} label="Running now" value={runningCount} sub={runningCount ? "in progress" : "idle"} tone={runningCount ? "info" : undefined} />
          <Metric icon={AlertTriangle} label="Needs attention" value={failedCount} sub={failedCount ? "last run failed" : "all clear"} tone={failedCount ? "crit" : "ok"} />
          <Metric icon={CheckCircle2} label="Success rate" value={`${stats.success_rate_30d ?? stats.success_rate ?? 0}%`} sub={`last 30 days · ${stats.runs_30d ?? 0} runs`} tone={(stats.success_rate_30d ?? 100) >= 90 ? "ok" : (stats.success_rate_30d ?? 100) >= 70 ? "warn" : "crit"} />
        </div>
      </section>}

      {notice && <div className="notice">{notice}</div>}

      {activeView === "automations" && <section className="system-strip">
        {nextRunItem
          ? <span><Clock3 size={15} /> Next: <strong>{nextRunItem.name}</strong> · {fmtDate(nextRunItem.next_run)}</span>
          : <span><Clock3 size={15} /> No scheduled runs queued</span>}
        <span><Activity size={15} /> {scheduledItems.length} scheduled · {onDemandCount} on-demand</span>
        {lastRunItem && <span><CheckCircle2 size={15} /> Last run: <strong>{lastRunItem.name}</strong> · {fmtDate(lastRunItem.last_run)}</span>}
      </section>}

      {activeView === "l10" && <L10ScorecardPage section={infraSection} setSection={setInfraSection} data={l10Scorecard} positives={positiveLeads} bounces={bounceEvents} health={infraHealth} domains={domainIntelligence} trends={infraTrends} period={effectiveMetricPeriod} periodLoading={periodLoading} workspaceFilter={workspaceFilter} workspaceOptions={workspaceOptions} excludeBaton={excludeBaton} platform={platform} dateFilter={dateFilter} filterKey={metricFilterKey} onPeriodChange={changeCalculationPeriod} onWorkspaceChange={changeWorkspaceFilter} onExcludeBatonChange={changeExcludeBaton} onPlatformChange={changePlatform} onDateFilterApply={applyDateFilter} onDateFilterClear={clearDateFilter} onFetchEvents={fetchEvents} onFetchDomains={fetchDomains} onFetchTrends={fetchTrends} onFetchCampaignBounces={fetchCampaignBounces} campaignBounces={campaignBounces} onExecuteDomain={isAdmin ? executeDomainLimitAction : undefined} executingDomain={executingDomain} onRun={isAdmin ? runL10Scorecard : undefined} running={l10Running} token={token} isAdmin={isAdmin} />}

      {activeView === "automations" && <section className="automations-page">
        {monitoringAutomations.length > 0 && (
          <section className="automation-section">
            <div className="section-kicker">Scheduled monitoring</div>
            <div className="automation-grid">
              {monitoringAutomations.map((automation) => (
                <AutomationCard key={automation.key} automation={automation} onRun={runAutomation} onStop={stopAutomationRun} running={busyKey === automation.key} stopping={stoppingRunId === automation.active_run_id} />
              ))}
            </div>
          </section>
        )}

        {reconnectAutomations.length > 0 && (
          <section className="automation-section">
            <div className="section-kicker">Reconnect operations</div>
            <div className="automation-grid">
              {reconnectAutomations.map((automation) => (
                <AutomationCard key={automation.key} automation={automation} onRun={runAutomation} onStop={stopAutomationRun} running={busyKey === automation.key} stopping={stoppingRunId === automation.active_run_id} />
              ))}
            </div>
          </section>
        )}

        {mailboxAutomations.length > 0 && (
          <section className="automation-section">
            <div className="section-kicker">Mailbox operations</div>
            <div className="automation-grid">
              {mailboxAutomations.map((automation) => (
                <AutomationCard key={automation.key} automation={automation} onRun={runAutomation} onStop={stopAutomationRun} running={busyKey === automation.key} stopping={stoppingRunId === automation.active_run_id} />
              ))}
            </div>
          </section>
        )}

        {lookupAutomations.length > 0 && (
          <section className="automation-section">
            <div className="section-kicker">Lookup and utilities</div>
            <div className="automation-grid">
              {lookupAutomations.map((automation) => (
                <AutomationCard key={automation.key} automation={automation} onRun={runAutomation} onStop={stopAutomationRun} running={busyKey === automation.key} stopping={stoppingRunId === automation.active_run_id} />
              ))}
            </div>
          </section>
        )}
      </section>}

      {activeView === "removal" && <DomainRemovalPage data={removalQueue} busy={removalBusy} onRefresh={fetchRemovalQueue} onCancel={cancelDomainRemoval} onConfirmRemove={confirmDomainRemove} candidates={removalCandidates} candidatesLoading={candidatesLoading} onScanCandidates={scanRemovalCandidates} onHold={holdRemovalDomain} onHoldAll={holdAllRemovalDomains} onRemoveAll={removeAllReadyDomains} />}

      {activeView === "domainage" && <DomainAgePage data={domainAgeRR} trend={domainAgeTrend} period={domainAgePeriod} loading={domainAgeLoading} onPeriod={setDomainAgePeriod} />}

      {activeView === "logs" && <LogsPanel logs={logs} selectedLog={selectedLog} setSelectedLog={setSelectedLog} onStopLog={stopAutomationLog} stoppingLogId={stoppingLogId} />}

      {activeView === "data" && <DataTablesPanel data={dataTables} selectedTable={selectedDataTable} onSelectTable={setSelectedDataTable} />}
    </main>
  );
}
